/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { AxisLeft, AxisRight, AxisBottom } from "@visx/axis";
import { Group } from "@visx/group";
import { LinePath, Line } from "@visx/shape";
import { scaleBand, scaleLinear, scaleTime } from "@visx/scale";
import { curveLinear } from "@visx/curve";
import { ParentSize } from "@visx/responsive";
import {
  IonSpinner,
  IonModal,
  IonIcon,
  useIonViewWillLeave,
  isPlatform,
} from "@ionic/react";
import { close } from "ionicons/icons";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import {
  getClearTherapySummary,
  ClearTherapyDataPoint,
  ClearTherapyVitalsPoint,
  ClearTherapySessionDetail,
} from "../../services/graphs";
import { TimePeriod } from "../../utils/dateHelpers";
import { getIonicStorage, ionicStorage } from "../../services/storageService";
import { useStorage } from "../../contexts/StorageContext";
import CustomDropdown from "../dropdown/CustomDropDown";
import "./InsightsGraph.css";
import { TimelineSlider } from "../../pages/activity/TimeLineSlider";
import { getResponsiveLayout } from "../../utils/helpers";

// ── Colors ────────────────────────────────────────────────────────────────────
const COLORS = {
  neb: "#00BCD4",
  pep: "#FF6B6B",
  osc: "#1565C0",
  grid: "#f0f0f0",
  axis: "#4A4A4A",
  cursor: "#1565C0",
};

const PRESSURE_PEP_COLOR = "#FF6B6B";
const PRESSURE_OSC_COLOR = "#4FC3F7";

// ── Unified metric config ─────────────────────────────────────────────────────
const METRIC_CONFIG = {
  avgPressure: {
    label: "Avg. Pressures",
    unit: "cm H₂O",
    scale: [0, 30] as [number, number],
    group: "pressure" as const,
  },
  spo2: {
    label: "SpO₂",
    unit: "SpO₂ (%)",
    color: "#E53935",
    scale: [85, 100] as [number, number],
    group: "vital" as const,
  },
  heartRate: {
    label: "Heart Rate",
    unit: "Heart Rate BPM",
    color: "#ef4444",
    scale: [40, 180] as [number, number],
    group: "vital" as const,
  },
  aqi: {
    label: "AQI",
    unit: "Air Quality Index",
    color: "#f59e0b",
    scale: [0, 500] as [number, number],
    group: "vital" as const,
  },
} as const;

type MetricKey = keyof typeof METRIC_CONFIG;

const METRIC_OPTIONS = Object.entries(METRIC_CONFIG).map(([value, cfg]) => ({
  label: cfg.label,
  value,
}));

// ── Types ─────────────────────────────────────────────────────────────────────
interface PressureStat {
  date: string;
  formattedDate: string;
  min: number;
  max: number;
  median: number;
}

interface ChartDataPoint extends ClearTherapyDataPoint {
  formattedDate: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseDateParts = (dateStr: string): [number, number, number] => {
  const [y, m, d] = dateStr.split("T")[0].split("-").map(Number);
  return [y, m, d];
};

const toYYYYMMDD = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

const normalizeDateKey = (dateStr: string): string =>
  dateStr.length === 7 ? dateStr + "-01" : dateStr.split("T")[0];

const generateAllDatesInRange = (
  startDate: string,
  endDate: string,
  timePeriod: TimePeriod,
): string[] => {
  const dates: string[] = [];
  const [sy, sm] = parseDateParts(startDate);
  const [ey, em] = parseDateParts(endDate);

  if (timePeriod === "Year") {
    const current = new Date(sy, sm - 1, 1);
    const endMonth = new Date(ey, em - 1, 1);
    while (current <= endMonth) {
      dates.push(toYYYYMMDD(current));
      current.setMonth(current.getMonth() + 1);
    }
  } else {
    const [, , sd] = parseDateParts(startDate);
    const [, , ed] = parseDateParts(endDate);
    const current = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    while (current <= end) {
      dates.push(toYYYYMMDD(current));
      current.setDate(current.getDate() + 1);
    }
  }
  return dates;
};

const formatDateLabel = (dateStr: string, timePeriod: TimePeriod): string => {
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  if (timePeriod === "Year") return MONTHS[month - 1];
  if (timePeriod === "Month") return String(day);
  return DAYS[new Date(year, month - 1, day).getDay()];
};

const formatFullDate = (dateStr: string, isYearly: boolean): string => {
  const [year, month, day] = parseDateParts(dateStr);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return isYearly
    ? `${months[month - 1]} ${year}`
    : `${day} ${months[month - 1]} ${year}`;
};

const getSessionLabel = (s: ClearTherapySessionDetail, idx: number): string => {
  const date = new Date(s.startTime || "");
  if (isNaN(date.getTime())) return `S${idx + 1}`;
  // UTC logic as before
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  const hours12 = hours % 12 || 12;
  return `${hours12.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm}`;
};
const getMedianNumber = (values: number[]): number => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
};

// Build a day-level aggregate from actual session rows so zoom-out can restore
// averages that reflect what was seen in drill-down.
const buildDailyAggregateFromSessions = (
  date: string,
  sessions: ClearTherapySessionDetail[],
): ClearTherapyDataPoint | null => {
  if (!sessions.length) return null;
  const count = sessions.length;
  const avgNebDuration =
    sessions.reduce((sum, s) => sum + (s.nebDuration || 0), 0) / count;
  const avgPepDuration =
    sessions.reduce((sum, s) => sum + (s.pepDuration || 0), 0) / count;
  const avgOscDuration =
    sessions.reduce((sum, s) => sum + (s.oscDuration || 0), 0) / count;
  const avgPepWithNebTime =
    sessions.reduce((sum, s) => sum + (s.pepWithNebTime || 0), 0) / count;
  const avgOscWithNebTime =
    sessions.reduce((sum, s) => sum + (s.oscWithNebTime || 0), 0) / count;

  const pepPoints = sessions
    .map((s) => s.pepPressure)
    .filter(
      (p): p is NonNullable<ClearTherapySessionDetail["pepPressure"]> =>
        p !== null,
    );
  const oscPoints = sessions
    .map((s) => s.oscPressure)
    .filter(
      (p): p is NonNullable<ClearTherapySessionDetail["oscPressure"]> =>
        p !== null,
    );

  const pepPressure =
    pepPoints.length > 0
      ? {
        min: Math.min(...pepPoints.map((p) => p.min)),
        max: Math.max(...pepPoints.map((p) => p.max)),
        median: getMedianNumber(pepPoints.map((p) => p.median)),
      }
      : null;

  const oscPressure =
    oscPoints.length > 0
      ? {
        min: Math.min(...oscPoints.map((p) => p.min)),
        max: Math.max(...oscPoints.map((p) => p.max)),
        median: getMedianNumber(oscPoints.map((p) => p.median)),
      }
      : null;

  return {
    date,
    avgNebDuration,
    avgPepDuration,
    avgOscDuration,
    avgPepWithNebTime,
    avgOscWithNebTime,
    pepPressure,
    oscPressure,
    summary: {
      sessionCount: count,
      modes: Array.from(new Set(sessions.map((s) => s.mode).filter(Boolean))),
    } as any,
  };
};

// Returns the nearest index in `data` that has actual therapy data.
// If `fromIndex` already has data, returns it unchanged.
// Otherwise searches outward until a non-zero bar is found.
const findNearestDataIndex = (
  data: ChartDataPoint[],
  fromIndex: number | null,
): number => {
  if (data.length === 0) return 0;
  const hasData = (d: ChartDataPoint) =>
    d.avgPepDuration > 0 || d.avgOscDuration > 0 || d.avgNebDuration > 0;
  const start =
    fromIndex !== null
      ? Math.max(0, Math.min(fromIndex, data.length - 1))
      : data.length - 1;
  if (hasData(data[start])) return start;
  for (let offset = 1; offset < data.length; offset++) {
    const lo = start - offset;
    const hi = start + offset;
    if (lo >= 0 && hasData(data[lo])) return lo;
    if (hi < data.length && hasData(data[hi])) return hi;
  }
  return start;
};

// ── Viewport type ─────────────────────────────────────────────────────────────
interface TimeViewport {
  start: Date;
  end: Date;
}

// ── Props ─────────────────────────────────────────────────────────────────────
export interface ClearPressureFlowGraphProps {
  profileId: number;
  dateRange: { startDate: string; endDate: string };
  className?: string;
  title?: string;
}

// ── Main Component ────────────────────────────────────────────────────────────
const ClearPressureFlowGraph: React.FC<ClearPressureFlowGraphProps> = ({
  profileId,
  dateRange,
  className = "mx-2 mb-4",
  title,
}) => {
  const [rawData, setRawData] = useState<ClearTherapyDataPoint[]>([]);
  const [vitalsData, setVitalsData] = useState<ClearTherapyVitalsPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedMetric, setSelectedMetric] =
    useState<MetricKey>("avgPressure");

  // ── Internal date range: self-contained period navigation ─────────────────
  const [displayDateRange, setDisplayDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>(dateRange);

  // ── Derive timePeriod from dateRange span (no prop needed) ─────────────────
  const timePeriod = useMemo((): TimePeriod => {
    const start = new Date(displayDateRange.startDate + "T00:00:00");
    const end = new Date(displayDateRange.endDate + "T00:00:00");
    const diffDays = Math.round(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (diffDays >= 300) return "Year";
    if (diffDays >= 20) return "Month";
    return "Week";
  }, [displayDateRange]);

  // ── Drill-down state (day sessions view) ──────────────────────────────────
  const [drillDownDate, setDrillDownDate] = useState<string | null>(null);
  const [daySessionsData, setDaySessionsData] = useState<
    ClearTherapySessionDetail[]
  >([]);
  const [daySessionsLoading, setDaySessionsLoading] = useState(false);
  const [savedActiveIndex, setSavedActiveIndex] = useState<number | null>(null);

  const [activeSessionRefId, setActiveSessionRefId] = useState<string | null>(
    null,
  );

  // ── Month drill-down state (year → daily bars) ────────────────────────────
  const [monthDrillDown, setMonthDrillDown] = useState<{
    date: string;
    data: ClearTherapyDataPoint[];
  } | null>(null);
  const [monthDrillDownLoading, setMonthDrillDownLoading] = useState(false);
  const [savedMonthActiveIndex, setSavedMonthActiveIndex] = useState<
    number | null
  >(null);

  // ── Viewport (visible time window within pre-fetched data) ───────────────
  const [viewport, setViewport] = useState<TimeViewport | null>(null);

  const isLargeScreen =
    typeof window !== "undefined" ? window.innerWidth > 480 : false;
  const aggregationLevel = timePeriod === "Year" ? "yearly" : "monthly";
  const isMobilePhone = useMemo(() => isPlatform("mobile"), []);

  // ── Fullscreen state ──────────────────────────────────────────────────────
  const [isFullscreen, setIsFullscreen] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const { storage, isStorageReady } = useStorage();

  // ── Wide data cache (viewport-driven: avoids re-fetch when navigating within same range) ──
  const dataCache = useRef<{
    therapyData: ClearTherapyDataPoint[];
    vitalsData: ClearTherapyVitalsPoint[];
    fetchStart: string;
    fetchEnd: string;
    timePeriod: TimePeriod;
  } | null>(null);

  // ── Year map: caches per-month daily data for instant month switching ──────
  const monthDrillDownCache = useRef<Map<string, ClearTherapyDataPoint[]>>(
    new Map(),
  );

  const [currentContext, setCurrentContext] = useState<Date>(() => {
    const end = new Date(dateRange.endDate + "T00:00:00");
    return isNaN(end.getTime()) ? new Date() : end;
  });
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);

  const currentMetricConfig = METRIC_CONFIG[selectedMetric];
  const showPressures = currentMetricConfig.group === "pressure";
  const vitalColor =
    !showPressures && "color" in currentMetricConfig
      ? ((currentMetricConfig as any).color as string)
      : "#666";

  // Sync internal range when parent changes period type or navigates externally
  useEffect(() => {
    setDisplayDateRange(dateRange);
    setActiveIndex(null);
  }, [dateRange.startDate, dateRange.endDate]);

  // Reset chart state whenever displayed period changes (parent or internal nav)
  useEffect(() => {
    setDrillDownDate(null);
    setDaySessionsData([]);
    setMonthDrillDown(null);
    setActiveSessionRefId(null);
    setActiveIndex(null);
    const end = new Date(displayDateRange.endDate + "T00:00:00");
    if (!isNaN(end.getTime())) setCurrentContext(end);
  }, [displayDateRange.startDate, displayDateRange.endDate]); // intentional: no drill-down deps needed here

  // Reset session selection when drill-down exits
  useEffect(() => {
    if (!drillDownDate) setActiveSessionRefId(null);
  }, [drillDownDate]);

  // ── Clear cache on patient change ─────────────────────────────────────────
  useEffect(() => {
    dataCache.current = null;
    monthDrillDownCache.current = new Map();
  }, [profileId]);

  // ── Clear month drill-down cache on year change ───────────────────────────
  useEffect(() => {
    monthDrillDownCache.current = new Map();
  }, [displayDateRange.startDate]);

  // ── Fetch top-level data (viewport-driven: pre-fetches wide range, slices for display) ──
  useEffect(() => {
    if (!profileId || !displayDateRange.startDate || !displayDateRange.endDate)
      return;

    const CACHE_VERSION = 1;

    // ── Compute wide fetch range ──
    let fetchStart: string;
    let fetchEnd: string;
    if (timePeriod === "Year") {
      const y = new Date(
        displayDateRange.startDate + "T00:00:00",
      ).getFullYear();
      fetchStart = `${y}-01-01`;
      fetchEnd = `${y}-12-31`;
    } else if (timePeriod === "Month") {
      // Fetch exactly the displayed month.
      // The BE uses a 45-day threshold to decide daily vs monthly aggregation.
      // A single month (28–31 days) is always below that threshold → daily data.
      fetchStart = displayDateRange.startDate;
      fetchEnd = displayDateRange.endDate;
    } else {
      fetchStart = displayDateRange.startDate;
      fetchEnd = displayDateRange.endDate;
    }

    const cacheKey = `clearTherapyGraphData_${profileId}_${fetchStart}_${fetchEnd}_${timePeriod}`;

    const applyData = (
      fullTherapy: ClearTherapyDataPoint[],
      fullVitals: ClearTherapyVitalsPoint[],
    ) => {
      dataCache.current = {
        therapyData: fullTherapy,
        vitalsData: fullVitals,
        fetchStart,
        fetchEnd,
        timePeriod,
      };

      const expandToMonthlyDaily = (data: any[]) => {
        const dailyMap = new Map<string, any>();
        data.forEach((item: any) => {
          const [year, month] = item.date.split("-");
          const daysInMonth = new Date(
            parseInt(year),
            parseInt(month),
            0,
          ).getDate();
          for (let day = 1; day <= daysInMonth; day++) {
            const dailyKey = `${year}-${month.padStart(2, "0")}-${day
              .toString()
              .padStart(2, "0")}`;
            dailyMap.set(dailyKey, item);
          }
        });
        return Array.from(dailyMap.values()).sort((a, b) =>
          a.date.localeCompare(b.date),
        );
      };

      const startKey = normalizeDateKey(displayDateRange.startDate);
      const endKey = normalizeDateKey(displayDateRange.endDate);
      const processedTherapy =
        timePeriod === "Year" ? expandToMonthlyDaily(fullTherapy) : fullTherapy;
      const processedVitals =
        timePeriod === "Year" ? expandToMonthlyDaily(fullVitals) : fullVitals;

      const filteredTherapy = processedTherapy.filter((d: any) => {
        const k = normalizeDateKey(d.date);
        return k >= startKey && k <= endKey;
      });
      // DEBUG: log what goes into rawData
      console.log("[ClearGraph] applyData: timePeriod:", timePeriod, "startKey:", startKey, "endKey:", endKey);
      console.log("[ClearGraph] filteredTherapy count:", filteredTherapy.length, "sample:", filteredTherapy.slice(0, 2).map((d: any) => ({
        date: d.date,
        normKey: normalizeDateKey(d.date),
        avgNebDuration: d.avgNebDuration,
        avgPepDuration: d.avgPepDuration,
      })));
      setRawData(filteredTherapy);
      setVitalsData(
        processedVitals.filter((v: any) => {
          const k = normalizeDateKey(v.date);
          return k >= startKey && k <= endKey;
        }),
      );
    };

    let cancelled = false;

    const fetchAndCache = async () => {
      // 1. In-memory cache hit
      const memCache = dataCache.current;
      if (
        memCache &&
        memCache.timePeriod === timePeriod &&
        displayDateRange.startDate >= memCache.fetchStart &&
        displayDateRange.endDate <= memCache.fetchEnd
      ) {
        const startKey = normalizeDateKey(displayDateRange.startDate);
        const endKey = normalizeDateKey(displayDateRange.endDate);
        // Fix: use normalizeDateKey(d.date) so "YYYY-MM" monthly dates compare
        // correctly against startKey/endKey ("YYYY-MM-DD"). Without this,
        // "2024-01" < "2024-01-01" (string comparison) so all monthly items
        // were excluded, producing an empty chart in Year view.
        const filteredTherapy = memCache.therapyData.filter((d) => {
          const k = normalizeDateKey(d.date);
          return k >= startKey && k <= endKey;
        });
        const filteredVitals = memCache.vitalsData.filter((v) => {
          const k = normalizeDateKey(v.date);
          return k >= startKey && k <= endKey;
        });
        console.log("[ClearGraph] MEM-CACHE hit — timePeriod:", timePeriod, "startKey:", startKey, "endKey:", endKey);
        console.log("[ClearGraph] MEM-CACHE filtered therapy count:", filteredTherapy.length, "sample:", filteredTherapy.slice(0, 2).map((d: any) => ({ date: d.date, normKey: normalizeDateKey(d.date), neb: d.avgNebDuration })));
        setRawData(filteredTherapy);
        setVitalsData(filteredVitals);
        return;
      }

      setLoading(true);

      // 2. Persistent cache hit (ionicStorage)
      if (storage && isStorageReady) {
        try {
          const cached = await getIonicStorage(storage, cacheKey);
          if (cached && cached.version === CACHE_VERSION && !cancelled) {
            applyData(cached.therapyData, cached.vitalsData);
            // Still fetch fresh data in background (don't setLoading again after)
          }
        } catch {
          // Ignore cache read errors
        }
      }

      // 3. Network fetch
      try {
        const res = await getClearTherapySummary(
          profileId,
          fetchStart,
          fetchEnd,
        );
        if (cancelled) return;
        const fullTherapy = res.therapyData || [];
        const fullVitals = res.vitalsData || [];
        // DEBUG: log raw API response for year view neb investigation
        console.log("[ClearGraph] timePeriod:", timePeriod, "fetchStart:", fetchStart, "fetchEnd:", fetchEnd);
        console.log("[ClearGraph] raw therapyData sample (first 3):", fullTherapy.slice(0, 3).map((d: any) => ({
          date: d.date,
          avgNebDuration: d.avgNebDuration,
          avgPepDuration: d.avgPepDuration,
          avgOscDuration: d.avgOscDuration,
          keys: Object.keys(d),
        })));
        applyData(fullTherapy, fullVitals);

        // 4. Persist to storage
        if (storage && isStorageReady) {
          await ionicStorage(storage, cacheKey, {
            therapyData: fullTherapy,
            vitalsData: fullVitals,
            cacheTimestamp: Date.now(),
            version: CACHE_VERSION,
          });
        }
      } catch (err) {
        console.error("ClearPressureFlowGraph fetch error:", err);
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAndCache();
    return () => {
      cancelled = true;
    };
  }, [
    profileId,
    displayDateRange.startDate,
    displayDateRange.endDate,
    timePeriod,
    storage,
    isStorageReady,
  ]);

  // ── Build chart data ───────────────────────────────────────────────────────
  const chartData: ChartDataPoint[] = useMemo(() => {
    const allDates = generateAllDatesInRange(
      displayDateRange.startDate,
      displayDateRange.endDate,
      timePeriod,
    );
    const dataMap = new Map<string, ClearTherapyDataPoint>();
    rawData.forEach((d) => dataMap.set(normalizeDateKey(d.date), d));

    // DEBUG: log dataMap keys vs allDates to check lookup
    if (timePeriod === "Year") {
      console.log("[ClearGraph] chartData build — allDates:", allDates);
      console.log("[ClearGraph] chartData build — dataMap keys:", Array.from(dataMap.keys()));
      console.log("[ClearGraph] chartData build — dataMap neb values:", Array.from(dataMap.entries()).map(([k, v]) => ({ key: k, neb: v.avgNebDuration, pep: v.avgPepDuration })));
    }

    return allDates.map((dateStr) => {
      const existing = dataMap.get(dateStr);
      const formattedDate = formatDateLabel(dateStr, timePeriod);
      if (existing)
        return {
          ...existing,
          formattedDate,
          pepPressure: existing.pepPressure ?? null,
          oscPressure: existing.oscPressure ?? null,
        };
      return {
        date: dateStr,
        avgNebDuration: 0,
        avgPepDuration: 0,
        avgOscDuration: 0,
        pepPressure: null,
        oscPressure: null,
        formattedDate,
      };
    });
  }, [rawData, displayDateRange, timePeriod]);

  // ── Calendar bounds (full range of loaded data) ────────────────────────────
  const fullCalendarBounds = useMemo<TimeViewport | null>(() => {
    if (!chartData.length) return null;
    return {
      start: new Date(normalizeDateKey(chartData[0].date) + "T00:00:00"),
      end: new Date(
        normalizeDateKey(chartData[chartData.length - 1].date) + "T23:59:59",
      ),
    };
  }, [chartData]);

  // Initialise / reset viewport when period or data changes
  useEffect(() => {
    if (fullCalendarBounds) {
      setViewport({
        start: fullCalendarBounds.start,
        end: fullCalendarBounds.end,
      });
    }
  }, [fullCalendarBounds?.start.getTime(), fullCalendarBounds?.end.getTime()]);

  // ── Max duration for left axis ─────────────────────────────────────────────
  const maxDuration = useMemo(
    () =>
      rawData.reduce(
        (m, d) =>
          Math.max(m, d.avgNebDuration + d.avgPepDuration + d.avgOscDuration),
        0,
      ),
    [rawData],
  );

  // ── Vitals map ─────────────────────────────────────────────────────────────
  const vitalsMap = useMemo(() => {
    const map = new Map<string, ClearTherapyVitalsPoint>();
    vitalsData.forEach((v) => map.set(normalizeDateKey(v.date), v));
    return map;
  }, [vitalsData]);

  // ── Pressure stats ─────────────────────────────────────────────────────────
  const pepStats = useMemo(
    (): PressureStat[] =>
      chartData
        .map((item) => {
          const stats = item.pepPressure ?? null;
          if (!stats) return null;
          return {
            date: item.date,
            formattedDate: item.formattedDate,
            ...stats,
          };
        })
        .filter((s): s is PressureStat => s !== null),
    [chartData],
  );

  const oscStats = useMemo(
    (): PressureStat[] =>
      chartData
        .map((item) => {
          const stats = item.oscPressure ?? null;
          if (!stats) return null;
          return {
            date: item.date,
            formattedDate: item.formattedDate,
            ...stats,
          };
        })
        .filter((s): s is PressureStat => s !== null),
    [chartData],
  );

  // ── Vital stats ────────────────────────────────────────────────────────────
  const metricStats = useMemo<PressureStat[]>(() => {
    if (showPressures) return [];
    return chartData
      .map((item) => {
        const vitalRow = vitalsMap.get(normalizeDateKey(item.date));
        if (!vitalRow) return null;
        const stats = (vitalRow as any)[selectedMetric];
        // Handle null/undefined gracefully - still create point but with null stats
        if (!stats || typeof stats !== "object") return null;
        return {
          date: item.date,
          formattedDate: item.formattedDate,
          min: stats.min ?? 0,
          max: stats.max ?? 0,
          median: stats.median ?? 0,
        };
      })
      .filter((s): s is PressureStat => s !== null);
  }, [chartData, selectedMetric, vitalsMap, showPressures]);

  // ── Init activeIndex ───────────────────────────────────────────────────────
  useEffect(() => {
    if (chartData.length > 0 && activeIndex === null) {
      const today = new Date();
      const todayMonthStr = `${today.getFullYear()}-${String(
        today.getMonth() + 1,
      ).padStart(2, "0")}`;
      const todayDayStr = `${todayMonthStr}-${String(today.getDate()).padStart(
        2,
        "0",
      )}`;
      const idx = chartData.findIndex((d) =>
        timePeriod === "Year"
          ? d.date.startsWith(todayMonthStr)
          : d.date === todayDayStr,
      );
      setActiveIndex(idx !== -1 ? idx : chartData.length - 1);
    }
  }, [chartData.length, activeIndex, timePeriod]);

  // ── Timeline data ──────────────────────────────────────────────────────────
  const timelineAggregatedData = useMemo(
    () =>
      rawData.map((d) => ({
        timestamp: new Date(normalizeDateKey(d.date) + "T00:00:00").getTime(),
      })),
    [rawData],
  );

  // Wide dataDateRange so the timeline is scrollable across past months
  // Capped at 6 months back to keep DOM node count reasonable (~180 day items max)
  const timelineDataRange = useMemo(() => {
    if (timePeriod === "Year") {
      return {
        min: new Date(displayDateRange.startDate + "T00:00:00"),
        max: new Date(displayDateRange.endDate + "T00:00:00"),
      };
    }
    // For Month / Week: 6 months back from today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sixMonthsAgo = new Date(today);
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return { min: sixMonthsAgo, max: today };
  }, [displayDateRange, timePeriod]);

  const handleActiveIndexChange = useCallback(
    (idx: number | null) => {
      setActiveIndex(idx);
      if (idx !== null && chartData[idx]) {
        const dateStr = normalizeDateKey(chartData[idx].date);
        const dt = new Date(dateStr + "T00:00:00");
        if (!isNaN(dt.getTime())) {
          setCurrentContext(dt);
          if (timePeriod !== "Year") {
            setSelectedDay(dt);
            setSelectedMonth(null);
          } else {
            setSelectedMonth(dt.getMonth());
            setSelectedDay(null);
          }
        }
      }
    },
    [timePeriod, chartData],
  );

  // ── Drill-down fetch & navigation ──────────────────────────────────────────
  const fetchDaySessionsData = useCallback(
    async (date: string) => {
      if (!profileId) return;
      setSavedActiveIndex(activeIndex);
      setDrillDownDate(date);
      setDaySessionsLoading(true);
      setActiveSessionRefId(null);
      try {
        const res = await getClearTherapySummary(profileId, date, date);
        const sessions = res.sessions || [];
        setDaySessionsData(sessions);
        setActiveIndex(sessions.length > 0 ? 0 : null);

        // Keep parent/day view aligned with what user just inspected in session drill-down.
        const aggregate = buildDailyAggregateFromSessions(date, sessions);
        if (aggregate) {
          setRawData((prev) => {
            const idx = prev.findIndex(
              (d) => normalizeDateKey(d.date) === date,
            );
            if (idx === -1)
              return [...prev, aggregate].sort((a, b) =>
                a.date.localeCompare(b.date),
              );
            const next = [...prev];
            next[idx] = { ...next[idx], ...aggregate };
            return next;
          });

          setMonthDrillDown((prev) => {
            if (!prev || !date.startsWith(prev.date)) return prev;
            const idx = prev.data.findIndex(
              (d) => normalizeDateKey(d.date) === date,
            );
            if (idx === -1)
              return {
                ...prev,
                data: [...prev.data, aggregate].sort((a, b) =>
                  a.date.localeCompare(b.date),
                ),
              };
            const nextData = [...prev.data];
            nextData[idx] = { ...nextData[idx], ...aggregate };
            return { ...prev, data: nextData };
          });
        }
      } catch {
        setDaySessionsData([]);
        setActiveIndex(null);
      } finally {
        setDaySessionsLoading(false);
      }
    },
    [profileId, activeIndex],
  );

  const fetchMonthDrillDown = useCallback(
    async (monthStr: string) => {
      const [year, month] = monthStr.split("-").map(Number);
      const startDate = `${year}-${String(month).padStart(2, "0")}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, "0")}-${String(
        lastDay,
      ).padStart(2, "0")}`;

      setSavedMonthActiveIndex(activeIndex);
      setDrillDownDate(null);
      setDaySessionsData([]);
      setSelectedDay(new Date(startDate + "T00:00:00"));

      // Check year-map cache first → instant switch, no loading spinner
      const cached = monthDrillDownCache.current.get(monthStr);
      if (cached) {
        setMonthDrillDown({ date: monthStr, data: cached });
        setActiveIndex(cached.length > 0 ? cached.length - 1 : 0);
        return;
      }

      setMonthDrillDownLoading(true);
      setMonthDrillDown(null);
      try {
        const res = await getClearTherapySummary(profileId, startDate, endDate);
        const data = res.therapyData || [];
        monthDrillDownCache.current.set(monthStr, data); // Store in year map
        setMonthDrillDown({ date: monthStr, data });
        setActiveIndex(data.length > 0 ? data.length - 1 : 0);
      } catch {
        setMonthDrillDown(null);
      } finally {
        setMonthDrillDownLoading(false);
      }
    },
    [profileId, activeIndex],
  );

  const handleTimelineItemClick = useCallback(
    (item: any, _index: number) => {
      const dt = new Date(item.timestamp);
      const year = dt.getFullYear();
      const month = dt.getMonth();
      const day = dt.getDate();
      setCurrentContext(dt);

      if (timePeriod === "Year") {
        const dataIdx = chartData.findIndex((d) => {
          const itemDate = new Date(d.date.split("T")[0] + "T00:00:00");
          return (
            itemDate.getFullYear() === year && itemDate.getMonth() === month
          );
        });
        if (dataIdx !== -1 && dataIdx === activeIndex) {
          // Same month already selected → DESELECT (matches TherapySetVsDelivered yearly: same month = deselect)
          setActiveIndex(null);
          setSelectedMonth(null);
          return;
        }
        // Different month → drill into month (matches TherapySetVsDelivered yearly→monthly zoom)
        if (dataIdx !== -1) {
          setSelectedMonth(month);
          setSelectedDay(null);
          fetchMonthDrillDown(chartData[dataIdx].date.substring(0, 7));
        }
      } else {
        const clickedStr = `${year}-${String(month + 1).padStart(
          2,
          "0",
        )}-${String(day).padStart(2, "0")}`;
        const rangeStart = new Date(displayDateRange.startDate + "T00:00:00");
        const rangeEnd = new Date(displayDateRange.endDate + "T00:00:00");

        if (dt < rangeStart || dt > rangeEnd) {
          // Navigate to the period containing this date
          if (timePeriod === "Month") {
            const newStart = new Date(year, month, 1);
            const newEnd = new Date(year, month + 1, 0);
            setDisplayDateRange({
              startDate: toYYYYMMDD(newStart),
              endDate: toYYYYMMDD(newEnd),
            });
          } else {
            // Week: center a 7-day window on the clicked date
            const newStart = new Date(year, month, day);
            const newEnd = new Date(year, month, day + 6);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (newEnd > today) newEnd.setTime(today.getTime());
            setDisplayDateRange({
              startDate: toYYYYMMDD(newStart),
              endDate: toYYYYMMDD(newEnd),
            });
          }
          setSelectedDay(dt);
        } else {
          // Any day in range → zoom to day sessions (matches TherapySetVsDelivered monthly→daily zoom)
          setSelectedDay(dt);
          setSelectedMonth(null);
          fetchDaySessionsData(clickedStr);
        }
      }
    },
    [
      timePeriod,
      chartData,
      displayDateRange,
      activeIndex,
      fetchMonthDrillDown,
      fetchDaySessionsData,
    ],
  );

  // ── Month drill-down chart data (daily bars for the drilled month) ──────────
  const monthDrillDownChartData = useMemo((): ChartDataPoint[] => {
    if (!monthDrillDown) return [];
    const [year, month] = monthDrillDown.date.split("-").map(Number);
    const lastDay = new Date(year, month, 0).getDate();
    const allDays: string[] = [];
    for (let d = 1; d <= lastDay; d++) {
      allDays.push(
        `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(
          2,
          "0",
        )}`,
      );
    }
    const dataMap = new Map<string, ClearTherapyDataPoint>();
    monthDrillDown.data.forEach((d) => dataMap.set(d.date, d));
    return allDays.map((dateStr) => {
      const existing = dataMap.get(dateStr);
      const formattedDate = String(parseInt(dateStr.split("-")[2], 10));
      if (existing)
        return {
          ...existing,
          formattedDate,
          pepPressure: existing.pepPressure ?? null,
          oscPressure: existing.oscPressure ?? null,
        };
      return {
        date: dateStr,
        avgNebDuration: 0,
        avgPepDuration: 0,
        avgOscDuration: 0,
        pepPressure: null,
        oscPressure: null,
        formattedDate,
      };
    });
  }, [monthDrillDown]);

  const exitMonthDrillDown = useCallback(() => {
    const monthKey = monthDrillDown?.date ?? null;
    setMonthDrillDown(null);
    setDrillDownDate(null);
    setDaySessionsData([]);
    if (monthKey) {
      const monthIdx = chartData.findIndex((d) =>
        normalizeDateKey(d.date).startsWith(monthKey),
      );
      setActiveIndex(
        monthIdx !== -1
          ? monthIdx
          : findNearestDataIndex(chartData, savedMonthActiveIndex),
      );
    } else {
      setActiveIndex(findNearestDataIndex(chartData, savedMonthActiveIndex));
    }
    setSavedMonthActiveIndex(null);
  }, [savedMonthActiveIndex, chartData, monthDrillDown]);

  const exitDrillDown = useCallback(() => {
    const currentDay = drillDownDate;
    setDrillDownDate(null);
    setDaySessionsData([]);
    if (currentDay && monthDrillDown) {
      // Exiting session drill-down back to month drill-down:
      // use monthDrillDownChartData so activeIndex maps correctly.
      const dayIdx = monthDrillDownChartData.findIndex(
        (d) => normalizeDateKey(d.date) === currentDay,
      );
      setActiveIndex(
        dayIdx !== -1
          ? dayIdx
          : monthDrillDownChartData.length > 0
            ? monthDrillDownChartData.length - 1
            : 0,
      );
    } else if (currentDay) {
      const dayIdx = chartData.findIndex(
        (d) => normalizeDateKey(d.date) === currentDay,
      );
      setActiveIndex(
        dayIdx !== -1
          ? dayIdx
          : findNearestDataIndex(chartData, savedActiveIndex),
      );
    } else {
      setActiveIndex(findNearestDataIndex(chartData, savedActiveIndex));
    }
    setSavedActiveIndex(null);
    setActiveSessionRefId(null);
  }, [savedActiveIndex, chartData, drillDownDate, monthDrillDown, monthDrillDownChartData]);

  const drillDownDateObj = useMemo(
    () => (drillDownDate ? new Date(drillDownDate + "T00:00:00") : null),
    [drillDownDate],
  );

  const handleDrillDownTimelineItemClick = useCallback(
    (item: any, _index: number) => {
      const clickedDate = new Date(item.timestamp);
      const clickedStr = `${clickedDate.getFullYear()}-${String(
        clickedDate.getMonth() + 1,
      ).padStart(2, "0")}-${String(clickedDate.getDate()).padStart(2, "0")}`;
      if (clickedStr === drillDownDate) {
        exitDrillDown();
      } else {
        setCurrentContext(clickedDate);
        setSelectedDay(clickedDate);
        fetchDaySessionsData(clickedStr);
      }
    },
    [drillDownDate, exitDrillDown, fetchDaySessionsData],
  );

  // ── Drill-down chart data (sessions mapped to ChartDataPoint shape) ─────────
  const drillDownChartData = useMemo(
    (): ChartDataPoint[] =>
      daySessionsData.map((s, idx) => ({
        date: s.refId,
        formattedDate: getSessionLabel(s, idx),
        avgNebDuration: s.nebDuration,
        avgPepDuration: s.pepDuration,
        avgOscDuration: s.oscDuration,
        pepPressure: s.pepPressure,
        oscPressure: s.oscPressure,
      })),
    [daySessionsData],
  );


  const monthDrillDownTimelineData = useMemo(
    () =>
      (monthDrillDown?.data || []).map((d) => ({
        timestamp: new Date(d.date + "T00:00:00").getTime(),
      })),
    [monthDrillDown],
  );

  const handleMonthDrillDownTimelineItemClick = useCallback(
    (item: any, _index: number) => {
      const clickedDate = new Date(item.timestamp);
      const clickedStr = `${clickedDate.getFullYear()}-${String(
        clickedDate.getMonth() + 1,
      ).padStart(2, "0")}-${String(clickedDate.getDate()).padStart(2, "0")}`;
      // Any day → zoom to day sessions (matches TherapySetVsDelivered monthly→daily: same/different both drill in)
      setSelectedDay(clickedDate);
      fetchDaySessionsData(clickedStr);
    },
    [fetchDaySessionsData],
  );

  // ── Active data for info panel ─────────────────────────────────────────────
  const activeData = useMemo(() => {
    const isDD = drillDownDate !== null && drillDownChartData.length > 0;
    const isMD = monthDrillDown !== null && !isDD;
    const effChartData = isDD
      ? drillDownChartData
      : isMD
        ? monthDrillDownChartData
        : chartData;
    const effPepStats: PressureStat[] = isDD
      ? drillDownChartData
        .filter((d) => d.pepPressure !== null)
        .map((d) => ({
          date: d.date,
          formattedDate: d.formattedDate,
          ...d.pepPressure!,
        }))
      : isMD
        ? monthDrillDownChartData
          .filter((d) => d.pepPressure !== null)
          .map((d) => ({
            date: d.date,
            formattedDate: d.formattedDate,
            ...d.pepPressure!,
          }))
        : pepStats;
    const effOscStats: PressureStat[] = isDD
      ? drillDownChartData
        .filter((d) => d.oscPressure !== null)
        .map((d) => ({
          date: d.date,
          formattedDate: d.formattedDate,
          ...d.oscPressure!,
        }))
      : isMD
        ? monthDrillDownChartData
          .filter((d) => d.oscPressure !== null)
          .map((d) => ({
            date: d.date,
            formattedDate: d.formattedDate,
            ...d.oscPressure!,
          }))
        : oscStats;
    if (activeIndex === null || !effChartData[activeIndex]) return null;
    const d = effChartData[activeIndex];
    const pepStat = effPepStats.find((s) => s.date === d.date) ?? null;
    const oscStat = effOscStats.find((s) => s.date === d.date) ?? null;
    const vitalStat =
      !isDD && !isMD
        ? metricStats.find((s) => s.formattedDate === d.formattedDate) ?? null
        : null;
    return {
      date: isDD
        ? `${d.formattedDate} — ${formatFullDate(drillDownDate!, false)}`
        : isMD
          ? formatFullDate(d.date, false)
          : formatFullDate(d.date, timePeriod === "Year"),
      durations: {
        neb: d.avgNebDuration,
        pep: d.avgPepDuration,
        osc: d.avgOscDuration,
        total: d.avgNebDuration + d.avgPepDuration + d.avgOscDuration,
      },
      pepStat,
      oscStat,
      vitalStat,
      summary: d.summary ?? null,
    };
  }, [
    activeIndex,
    chartData,
    monthDrillDownChartData,
    drillDownChartData,
    pepStats,
    oscStats,
    metricStats,
    timePeriod,
    drillDownDate,
    monthDrillDown,
  ]);

  const displayTitle = title || "Therapy Details";

  // ── Period navigation (self-contained prev/next for month/year/week) ────────
  const canGoNext = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(displayDateRange.endDate + "T00:00:00");
    return endDate < today;
  }, [displayDateRange.endDate]);

  const goToPrev = useCallback(() => {
    const start = new Date(displayDateRange.startDate + "T00:00:00");
    const end = new Date(displayDateRange.endDate + "T00:00:00");
    let newStart: Date, newEnd: Date;
    if (timePeriod === "Year") {
      const y = start.getFullYear() - 1;
      newStart = new Date(y, 0, 1);
      newEnd = new Date(y, 11, 31);
    } else if (timePeriod === "Month") {
      // Sliding window: shift the current range back by SLIDE_STEP days,
      // keeping the same window size (overlaps with the previous period).
      const SLIDE_STEP = 10;
      newStart = new Date(start);
      newStart.setDate(newStart.getDate() - SLIDE_STEP);
      newEnd = new Date(end);
      newEnd.setDate(newEnd.getDate() - SLIDE_STEP);
    } else {
      newStart = new Date(start);
      newStart.setDate(newStart.getDate() - 7);
      newEnd = new Date(end);
      newEnd.setDate(newEnd.getDate() - 7);
    }
    setDisplayDateRange({
      startDate: toYYYYMMDD(newStart),
      endDate: toYYYYMMDD(newEnd),
    });
  }, [displayDateRange, timePeriod]);

  const goToNext = useCallback(() => {
    if (!canGoNext) return;
    const start = new Date(displayDateRange.startDate + "T00:00:00");
    const end = new Date(displayDateRange.endDate + "T00:00:00");
    let newStart: Date, newEnd: Date;
    if (timePeriod === "Year") {
      const y = start.getFullYear() + 1;
      newStart = new Date(y, 0, 1);
      newEnd = new Date(y, 11, 31);
    } else if (timePeriod === "Month") {
      // Sliding window: shift forward by SLIDE_STEP days, keeping window size.
      const SLIDE_STEP = 10;
      newStart = new Date(start);
      newStart.setDate(newStart.getDate() + SLIDE_STEP);
      newEnd = new Date(end);
      newEnd.setDate(newEnd.getDate() + SLIDE_STEP);
    } else {
      newStart = new Date(start);
      newStart.setDate(newStart.getDate() + 7);
      newEnd = new Date(end);
      newEnd.setDate(newEnd.getDate() + 7);
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (newEnd > today) newEnd = today;
    setDisplayDateRange({
      startDate: toYYYYMMDD(newStart),
      endDate: toYYYYMMDD(newEnd),
    });
  }, [canGoNext, displayDateRange, timePeriod]);

  // ── Gesture handlers ───────────────────────────────────────────────────────
  const resetDragPan = useCallback(() => {
    dragPanRef.current = null;
  }, []);

  // ── Session navigation ─────────────────────────────────────────────────────
  const findNextSession = useCallback(
    (currentRefId: string | null): ClearTherapySessionDetail | null => {
      if (!currentRefId) return daySessionsData[0] ?? null;
      const idx = daySessionsData.findIndex((s) => s.refId === currentRefId);
      if (idx === -1 || idx >= daySessionsData.length - 1) return null;
      return daySessionsData[idx + 1];
    },
    [daySessionsData],
  );

  const findPreviousSession = useCallback(
    (currentRefId: string | null): ClearTherapySessionDetail | null => {
      if (!currentRefId)
        return daySessionsData[daySessionsData.length - 1] ?? null;
      const idx = daySessionsData.findIndex((s) => s.refId === currentRefId);
      if (idx <= 0) return null;
      return daySessionsData[idx - 1];
    },
    [daySessionsData],
  );

  const handleChartSwipeLeft = useCallback(() => {
    // Use drillDownDate alone — not drillDownChartData.length — so that an empty-session
    // day stays in drill-down mode and doesn't fall through to top-level period navigation.
    const isDD = drillDownDate !== null;
    if (isDD) {
      // Navigate between sessions only when sessions are actually loaded
      if (drillDownChartData.length > 0) {
        const next = findNextSession(activeSessionRefId);
        if (next) {
          setActiveSessionRefId(next.refId);
          return;
        }
      }
      // At last session (or empty day): move exactly ONE calendar day forward
      if (monthDrillDown && drillDownDate) {
        const currentDayIdx = monthDrillDownChartData.findIndex(
          (d) => normalizeDateKey(d.date) === drillDownDate,
        );
        if (currentDayIdx !== -1 && currentDayIdx + 1 < monthDrillDownChartData.length) {
          fetchDaySessionsData(monthDrillDownChartData[currentDayIdx + 1].date);
          return;
        }
      } else if (drillDownDate) {
        // No month drill-down: advance one day in top-level chartData
        const currentDayIdx = chartData.findIndex(
          (d) => normalizeDateKey(d.date) === drillDownDate,
        );
        if (currentDayIdx !== -1 && currentDayIdx + 1 < chartData.length) {
          fetchDaySessionsData(chartData[currentDayIdx + 1].date);
          return;
        }
      }
      exitDrillDown();
      return;
    }
    if (activeIndex === null) return;
    const isMD = monthDrillDown !== null && !isDD;
    // Top-level view: swipe left navigates to next period (revealing future via dragging left)
    if (!isMD) {
      if (timePeriod === "Year") return;
      goToNext();
      return;
    }
    // Month drill-down: swipe navigates to next month
    if (monthDrillDown) {
      const [year, month] = monthDrillDown.date.split("-").map(Number);
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;
      const nextMonthStr = `${nextYear}-${String(nextMonth).padStart(2, "0")}`;
      // Don't navigate beyond the current date
      const today = new Date();
      const nextMonthDate = new Date(nextYear, nextMonth - 1, 1);
      if (nextMonthDate > today) return;
      fetchMonthDrillDown(nextMonthStr);
    }
  }, [
    activeIndex,
    drillDownDate,
    drillDownChartData,
    monthDrillDown,
    monthDrillDownChartData,
    chartData,
    goToNext,
    activeSessionRefId,
    findNextSession,
    exitDrillDown,
    timePeriod,
    fetchMonthDrillDown,
    fetchDaySessionsData,
  ]);

  const handleChartSwipeRight = useCallback(() => {
    // Same isDD fix as handleChartSwipeLeft — keep empty-day drill-downs in drill-down mode.
    const isDD = drillDownDate !== null;
    if (isDD) {
      // Navigate between sessions only when sessions are actually loaded
      if (drillDownChartData.length > 0) {
        const prev = findPreviousSession(activeSessionRefId);
        if (prev) {
          setActiveSessionRefId(prev.refId);
          return;
        }
      }
      // At first session (or empty day): move exactly ONE calendar day backward
      if (monthDrillDown && drillDownDate) {
        const currentDayIdx = monthDrillDownChartData.findIndex(
          (d) => normalizeDateKey(d.date) === drillDownDate,
        );
        if (currentDayIdx > 0) {
          fetchDaySessionsData(monthDrillDownChartData[currentDayIdx - 1].date);
          return;
        }
      } else if (drillDownDate) {
        // No month drill-down: go back one day in top-level chartData
        const currentDayIdx = chartData.findIndex(
          (d) => normalizeDateKey(d.date) === drillDownDate,
        );
        if (currentDayIdx > 0) {
          fetchDaySessionsData(chartData[currentDayIdx - 1].date);
          return;
        }
      }
      exitDrillDown();
      return;
    }
    if (activeIndex === null) return;
    const isMD = monthDrillDown !== null && !isDD;
    // Top-level view: swipe right navigates to previous period (revealing past via dragging right)
    if (!isMD) {
      if (timePeriod === "Year") return;
      goToPrev();
      return;
    }
    // Month drill-down: swipe navigates to previous month
    if (monthDrillDown) {
      const [year, month] = monthDrillDown.date.split("-").map(Number);
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      const prevMonthStr = `${prevYear}-${String(prevMonth).padStart(2, "0")}`;
      fetchMonthDrillDown(prevMonthStr);
    }
  }, [
    activeIndex,
    drillDownDate,
    drillDownChartData,
    monthDrillDown,
    monthDrillDownChartData,
    chartData,
    goToPrev,
    activeSessionRefId,
    findPreviousSession,
    exitDrillDown,
    timePeriod,
    fetchMonthDrillDown,
    fetchDaySessionsData,
  ]);

  const handleChartZoomIn = useCallback(
    (anchorIdx?: number) => {
      const isDD = drillDownDate !== null;
      const isMD = monthDrillDown !== null && !isDD;
      if (isDD) return;
      // Use anchor index when available; fall back to activeIndex, then latest index.
      const fallbackIdx =
        activeIndex ?? (isMD ? monthDrillDownChartData : chartData).length - 1;
      const rawTargetIdx = anchorIdx !== undefined ? anchorIdx : fallbackIdx;
      if (isMD) {
        const nearestIdx = findNearestDataIndex(
          monthDrillDownChartData,
          rawTargetIdx ?? null,
        );
        if (monthDrillDownChartData[nearestIdx]?.date) {
          fetchDaySessionsData(monthDrillDownChartData[nearestIdx].date);
        }
      } else if (timePeriod === "Year") {
        const nearestIdx = findNearestDataIndex(
          chartData,
          rawTargetIdx ?? null,
        );
        if (chartData[nearestIdx]?.date) {
          fetchMonthDrillDown(chartData[nearestIdx].date.substring(0, 7));
        }
      } else {
        const nearestIdx = findNearestDataIndex(
          chartData,
          rawTargetIdx ?? null,
        );
        if (chartData[nearestIdx]?.date) {
          fetchDaySessionsData(chartData[nearestIdx].date);
        }
      }
    },
    [
      drillDownDate,
      drillDownChartData,
      monthDrillDown,
      monthDrillDownChartData,
      timePeriod,
      activeIndex,
      chartData,
      fetchDaySessionsData,
      fetchMonthDrillDown,
    ],
  );

  const handleChartZoomOut = useCallback(() => {
    // Use drillDownDate as source-of-truth so zoom-out still works
    // while sessions are loading or when a day has zero sessions.
    const isDD = drillDownDate !== null;
    const isMD = monthDrillDown !== null && !isDD;
    if (isDD) exitDrillDown();
    else if (isMD) exitMonthDrillDown();
  }, [
    drillDownDate,
    monthDrillDown,
    exitDrillDown,
    exitMonthDrillDown,
  ]);

  // ── Drag pan handler (shifts viewport within calendar bounds) ────────────
  const dragPanRef = useRef<{ lastDeltaX: number } | null>(null);

  const handleChartPan = useCallback(
    (deltaX: number, chartWidth: number) => {
      if (!viewport || !fullCalendarBounds || chartWidth === 0) return;
      if (timePeriod === "Year") return;

      // Only act when pan crosses a meaningful threshold (avoid jitter)
      if (dragPanRef.current === null) {
        dragPanRef.current = { lastDeltaX: deltaX };
        return;
      }
      const moveDelta = deltaX - dragPanRef.current.lastDeltaX;
      dragPanRef.current.lastDeltaX = deltaX;

      const viewportMs = viewport.end.getTime() - viewport.start.getTime();
      const msPerPx = viewportMs / chartWidth;
      const shiftMs = -moveDelta * msPerPx;

      // Snap to one period unit
      const periodMs =
        aggregationLevel === "yearly"
          ? 30 * 24 * 60 * 60 * 1000 // ~1 month
          : 24 * 60 * 60 * 1000; // 1 day
      const periods = Math.round(shiftMs / periodMs);
      if (periods === 0) return;
      const actualShiftMs = periods * periodMs;

      let newStart = new Date(viewport.start.getTime() + actualShiftMs);
      let newEnd = new Date(viewport.end.getTime() + actualShiftMs);

      // Clamp to calendar bounds
      if (newStart < fullCalendarBounds.start) {
        const diff = newEnd.getTime() - newStart.getTime();
        newStart = new Date(fullCalendarBounds.start);
        newEnd = new Date(fullCalendarBounds.start.getTime() + diff);
      }
      if (newEnd > fullCalendarBounds.end) {
        const diff = newEnd.getTime() - newStart.getTime();
        newEnd = new Date(fullCalendarBounds.end);
        newStart = new Date(fullCalendarBounds.end.getTime() - diff);
      }

      setViewport({ start: newStart, end: newEnd });

      // Sync activeIndex to viewport center
      const centerTime = (newStart.getTime() + newEnd.getTime()) / 2;
      // Inline drill-down checks to avoid forward reference
      const isDD_ = drillDownDate !== null && drillDownChartData.length > 0;
      const isMD_ = monthDrillDown !== null && !isDD_;
      const activeData = isDD_
        ? drillDownChartData
        : isMD_
          ? monthDrillDownChartData
          : chartData;
      let nearestIdx = activeIndex ?? 0;
      let minDiff = Infinity;
      activeData.forEach((d, idx) => {
        const diff = Math.abs(
          new Date(normalizeDateKey(d.date) + "T00:00:00").getTime() -
          centerTime,
        );
        if (diff < minDiff) {
          minDiff = diff;
          nearestIdx = idx;
        }
      });
      if (nearestIdx !== activeIndex) handleActiveIndexChange(nearestIdx);
    },
    [
      viewport,
      fullCalendarBounds,
      timePeriod,
      aggregationLevel,
      drillDownDate,
      monthDrillDown,
      drillDownChartData,
      monthDrillDownChartData,
      chartData,
      activeIndex,
      handleActiveIndexChange,
    ],
  );

  // ── Smooth zoom navigation (matching TherapySetVsDelivered) ───────────────
  const handleTimelineWheelZoom = useCallback(
    (direction: "in" | "out") => {
      const isDD = drillDownDate !== null;
      const isMD = monthDrillDown !== null && !isDD;

      if (direction === "in") {
        // Zoom IN
        if (isMD) {
          // Month drill-down → Day sessions
          const targetDay = selectedDay || currentContext;
          const dayStr = toYYYYMMDD(targetDay);
          fetchDaySessionsData(dayStr);
        } else if (timePeriod === "Year") {
          // Year → Month drill-down
          const targetMonth = currentContext;
          const monthStr = `${targetMonth.getFullYear()}-${String(targetMonth.getMonth() + 1).padStart(2, "0")}`;
          fetchMonthDrillDown(monthStr);
        } else {
          // Month/Week → Day sessions
          const targetDay = selectedDay || currentContext;
          const dayStr = toYYYYMMDD(targetDay);
          fetchDaySessionsData(dayStr);
        }
      } else {
        // Zoom OUT
        if (isDD) {
          // Day sessions → back to month/year view
          exitDrillDown();
        } else if (isMD) {
          // Month drill-down → back to year view
          exitMonthDrillDown();
        } else if (timePeriod !== "Year") {
          // Month/Week view → Year view
          const yearStart = new Date(currentContext.getFullYear(), 0, 1);
          setCurrentContext(yearStart);
          setSelectedDay(null);
          setSelectedMonth(null);
          // Update display date range to full year
          const yearStr = String(currentContext.getFullYear());
          setDisplayDateRange({
            startDate: `${yearStr}-01-01`,
            endDate: `${yearStr}-12-31`,
          });
        }
      }
    },
    [
      drillDownDate,
      monthDrillDown,
      timePeriod,
      currentContext,
      selectedDay,
      fetchDaySessionsData,
      fetchMonthDrillDown,
      exitDrillDown,
      exitMonthDrillDown,
    ],
  );

  // ── Timeline scroll → sync activeIndex to visible center date ──────────────
  // Fires when the timeline is scrolled. Updates currentContext and activeIndex
  // so the chart and info panel always reflect what's visible in the timeline.
  const handleTimelineScroll = useCallback(
    (start: Date, end: Date) => {
      // Don't sync during drill-down modes
      if (drillDownDate !== null || monthDrillDown !== null) return;

      const centerTime = (start.getTime() + end.getTime()) / 2;
      const centerDate = new Date(centerTime);

      // Find nearest data point
      let nearestIdx = -1;
      let minDiff = Infinity;
      chartData.forEach((d, idx) => {
        const dDate = new Date(normalizeDateKey(d.date) + "T00:00:00");
        const diff = Math.abs(dDate.getTime() - centerDate.getTime());
        if (diff < minDiff) {
          minDiff = diff;
          nearestIdx = idx;
        }
      });

      if (nearestIdx !== -1 && nearestIdx !== activeIndex) {
        setActiveIndex(nearestIdx);
        const dt = new Date(
          normalizeDateKey(chartData[nearestIdx].date) + "T00:00:00",
        );
        if (!isNaN(dt.getTime())) {
          setCurrentContext(dt);
          // Only update selectedDay if we're in daily/weekly view
          if (timePeriod !== "Year") {
            setSelectedDay(dt);
          } else {
            // In year view, update selectedMonth based on the scrolled month
            setSelectedMonth(dt.getMonth());
          }
        }
      }
    },
    [drillDownDate, monthDrillDown, chartData, activeIndex, timePeriod],
  );

  // ── Period navigation (self-contained prev/next for month/year/week) ────────
  const periodLabel = useMemo(() => {
    const MONTHS = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    if (timePeriod === "Year") {
      return String(
        new Date(displayDateRange.startDate + "T00:00:00").getFullYear(),
      );
    }
    if (timePeriod === "Month") {
      const s = new Date(displayDateRange.startDate + "T00:00:00");
      const e = new Date(displayDateRange.endDate + "T00:00:00");
      if (
        s.getMonth() === e.getMonth() &&
        s.getFullYear() === e.getFullYear()
      ) {
        return `${MONTHS[s.getMonth()]} ${s.getFullYear()}`;
      }
      const fmt = (d: Date) =>
        `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
      return `${fmt(s)} – ${fmt(e)} ${e.getFullYear()}`;
    }
    // Week
    const s = new Date(displayDateRange.startDate + "T00:00:00");
    const e = new Date(displayDateRange.endDate + "T00:00:00");
    const fmt = (d: Date) =>
      `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
    return `${fmt(s)} – ${fmt(e)} ${e.getFullYear()}`;
  }, [displayDateRange, timePeriod]);

  // ── Effective data (switches between normal / month drill-down) ────────────
  // isInDrillDown: true as soon as user clicks a day (includes loading state) — drives UI
  // isDrillDown:   true only once sessions have loaded — drives data selection
  const isInDrillDown = drillDownDate !== null;
  const isDrillDown = drillDownDate !== null && drillDownChartData.length > 0;
  const isMonthDrillDown = monthDrillDown !== null && !isInDrillDown;

  // ── Effective context label for below the chart x-axis ──────────────────────
  // Computes the right label for every drill-down level without duplicating MONTHS.
  const effectiveContextLabel = useMemo(() => {
    if (isInDrillDown && drillDownDate) {
      // Session drill-down: show the day being inspected (e.g. "20 March 2026")
      const d = new Date(drillDownDate + "T00:00:00");
      return d.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
    }
    if (isMonthDrillDown && monthDrillDown) {
      // Month drill-down from Year: show "March 2026"
      const d = new Date(monthDrillDown.date + "-01T00:00:00");
      return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    }
    // Top-level: reuse periodLabel ("2026", "March 2026", "10 Mar – 20 Mar 2026", etc.)
    return periodLabel;
  }, [isInDrillDown, drillDownDate, isMonthDrillDown, monthDrillDown, periodLabel]);

  // ── Year-map markers for the timeline when in month drill-down ─────────────
  // One marker per unique month that has data (not 31 duplicates per month from expandToMonthlyDaily)
  // PLUS one marker per day from every cached month (so the timeline shows day-level dots).
  const computedYearMarkers = useMemo(() => {
    if (!isMonthDrillDown) return timelineAggregatedData;

    const markers: { timestamp: number }[] = [];

    // One entry per unique month with data (avoids the 31-identical-timestamps bug)
    const seenMonths = new Set<string>();
    rawData.forEach((d) => {
      const monthKey = normalizeDateKey(d.date).substring(0, 7); // "YYYY-MM"
      if (!seenMonths.has(monthKey)) {
        seenMonths.add(monthKey);
        markers.push({
          timestamp: new Date(normalizeDateKey(d.date) + "T00:00:00").getTime(),
        });
      }
    });

    // Daily markers from every month already fetched into the cache
    monthDrillDownCache.current.forEach((data) => {
      data.forEach((d) => {
        markers.push({ timestamp: new Date(d.date + "T00:00:00").getTime() });
      });
    });

    return markers;
    // monthDrillDown as dep so it recomputes each time a new month is loaded into cache
  }, [isMonthDrillDown, rawData, monthDrillDown, timelineAggregatedData]);

  // When in month drill-down, scope the chart x-axis to just that month (not the full year)
  const activeViewport =
    isMonthDrillDown && monthDrillDown
      ? (() => {
        const [y, m] = monthDrillDown.date.split("-").map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        return {
          start: new Date(`${monthDrillDown.date}-01T00:00:00`),
          end: new Date(
            `${monthDrillDown.date}-${String(lastDay).padStart(
              2,
              "0",
            )}T23:59:59`,
          ),
        };
      })()
      : viewport;

  // Use isInDrillDown (not isDrillDown) so an empty-session day shows
  // drillDownChartData=[] → the chart renders its "No Therapy Data" empty state
  // instead of falling back to the year/month chartData.
  const activeChartData = isInDrillDown
    ? drillDownChartData
    : isMonthDrillDown
      ? monthDrillDownChartData
      : chartData;
  const activePepStats: PressureStat[] = isInDrillDown
    ? drillDownChartData
      .filter((d) => d.pepPressure !== null)
      .map((d) => ({
        date: d.date,
        formattedDate: d.formattedDate,
        ...d.pepPressure!,
      }))
    : isMonthDrillDown
      ? monthDrillDownChartData
        .filter((d) => d.pepPressure !== null)
        .map((d) => ({
          date: d.date,
          formattedDate: d.formattedDate,
          ...d.pepPressure!,
        }))
      : pepStats;
  const activeOscStats: PressureStat[] = isInDrillDown
    ? drillDownChartData
      .filter((d) => d.oscPressure !== null)
      .map((d) => ({
        date: d.date,
        formattedDate: d.formattedDate,
        ...d.oscPressure!,
      }))
    : isMonthDrillDown
      ? monthDrillDownChartData
        .filter((d) => d.oscPressure !== null)
        .map((d) => ({
          date: d.date,
          formattedDate: d.formattedDate,
          ...d.oscPressure!,
        }))
      : oscStats;
  const activeMaxDuration = isInDrillDown
    ? drillDownChartData.reduce(
      (m, d) =>
        Math.max(m, d.avgNebDuration + d.avgPepDuration + d.avgOscDuration),
      0,
    )
    : isMonthDrillDown
      ? monthDrillDownChartData.reduce(
        (m, d) =>
          Math.max(m, d.avgNebDuration + d.avgPepDuration + d.avgOscDuration),
        0,
      )
      : maxDuration;
  // ── Vitals for month drill-down (per-day from vitalsMap) ──────────────────
  const monthDrillDownMetricStats = useMemo<PressureStat[]>(() => {
    if (!monthDrillDown || showPressures) return [];
    return monthDrillDownChartData
      .map((item) => {
        const vitalRow = vitalsMap.get(normalizeDateKey(item.date));
        if (!vitalRow) return null;
        const stats = (vitalRow as any)[selectedMetric];
        if (!stats || typeof stats !== "object") return null;
        return {
          date: item.date,
          formattedDate: item.formattedDate,
          min: stats.min ?? 0,
          max: stats.max ?? 0,
          median: stats.median ?? 0,
        };
      })
      .filter((s): s is PressureStat => s !== null);
  }, [
    monthDrillDown,
    monthDrillDownChartData,
    selectedMetric,
    vitalsMap,
    showPressures,
  ]);

  // ── Effective vitals stats: correct set for the active view ───────────────
  // Session drill-down: no vitals (daily vitals don't align to session x-axis labels)
  // Month drill-down: per-day vitals from vitalsMap
  // Top-level: regular metricStats from chartData
  const activeMetricStats: PressureStat[] = isInDrillDown
    ? []
    : isMonthDrillDown
      ? monthDrillDownMetricStats
      : metricStats;

  // ── Right axis range (uses active stats, clamped to config bounds) ─────────
  const metricRange = useMemo<[number, number]>(() => {
    const [configMin, configMax] = currentMetricConfig.scale;

    if (showPressures) {
      const effPep = isInDrillDown
        ? drillDownChartData
          .filter((d) => d.pepPressure !== null)
          .map((d) => ({ ...d.pepPressure! }))
        : isMonthDrillDown
          ? monthDrillDownChartData
            .filter((d) => d.pepPressure !== null)
            .map((d) => ({ ...d.pepPressure! }))
          : pepStats;
      const effOsc = isInDrillDown
        ? drillDownChartData
          .filter((d) => d.oscPressure !== null)
          .map((d) => ({ ...d.oscPressure! }))
        : isMonthDrillDown
          ? monthDrillDownChartData
            .filter((d) => d.oscPressure !== null)
            .map((d) => ({ ...d.oscPressure! }))
          : oscStats;
      const allStats = [...effPep, ...effOsc];
      if (!allStats.length) return currentMetricConfig.scale;
      const minVal = Math.min(...allStats.map((d) => d.min));
      const maxVal = Math.max(...allStats.map((d) => d.max));
      const range = maxVal - minVal;
      const pad = range > 0 ? range * 0.1 : 5;
      return [
        Math.max(0, Math.floor(minVal - pad) / 5) * 5,
        Math.ceil((maxVal + pad) / 5) * 5,
      ];
    }

    // Vitals: use active stats and clamp to config scale bounds
    const stats = isInDrillDown
      ? []
      : isMonthDrillDown
        ? monthDrillDownMetricStats
        : metricStats;
    if (!stats.length) return currentMetricConfig.scale;
    const minVal = Math.min(...stats.map((d) => d.min));
    const maxVal = Math.max(...stats.map((d) => d.max));
    const range = maxVal - minVal;
    const pad = range > 0 ? range * 0.1 : 5;
    return [
      Math.max(configMin, Math.floor((minVal - pad) / 5) * 5),
      Math.min(configMax, Math.ceil((maxVal + pad) / 5) * 5),
    ];
  }, [
    pepStats,
    oscStats,
    metricStats,
    monthDrillDownMetricStats,
    showPressures,
    currentMetricConfig.scale,
    isInDrillDown,
    isMonthDrillDown,
    drillDownChartData,
    monthDrillDownChartData,
  ]);

  // ── Selected session detail (for drill-down info panel) ───────────────────
  const activeSessionDetail = useMemo(() => {
    if (!isDrillDown || !activeSessionRefId) return null;
    return daySessionsData.find((s) => s.refId === activeSessionRefId) ?? null;
  }, [isDrillDown, activeSessionRefId, daySessionsData]);

  // ── Fullscreen / orientation handlers ────────────────────────────────────
  const handleLockLandscape = useCallback(async () => {
    try {
      if (ScreenOrientation?.lock) {
        await ScreenOrientation.lock({ orientation: "landscape" });
      }
    } catch (e) {
      console.warn("Failed to lock to landscape:", e);
    }
  }, []);

  const handleUnlockOrientation = useCallback(async () => {
    try {
      if (ScreenOrientation?.unlock) {
        await ScreenOrientation.unlock();
      }
    } catch (e) {
      console.warn("Failed to unlock orientation:", e);
    }
  }, []);

  const openFullscreen = useCallback(async () => {
    setIsFullscreen(true);
    await handleLockLandscape();
  }, [handleLockLandscape]);

  const closeFullscreen = useCallback(async () => {
    await handleUnlockOrientation();
    setIsFullscreen(false);
  }, [handleUnlockOrientation]);

  const handlePageLeave = useCallback(async () => {
    await handleUnlockOrientation();
    if (isFullscreen) {
      setIsFullscreen(false);
    }
  }, [handleUnlockOrientation, isFullscreen]);

  useIonViewWillLeave(() => {
    handlePageLeave();
  });

  // Lock/unlock orientation when fullscreen state changes
  useEffect(() => {
    const handleOrientationChange = async () => {
      if (isFullscreen) {
        await handleLockLandscape();
      } else {
        await handleUnlockOrientation();
      }
    };
    handleOrientationChange();
    return () => {
      handleUnlockOrientation();
    };
  }, [isFullscreen, handleLockLandscape, handleUnlockOrientation]);

  // Intercept hardware back button when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      const handleIonBack = (ev: any) => {
        ev.detail.register(100, () => {
          closeFullscreen();
        });
      };
      document.addEventListener("ionBackButton", handleIonBack);
      return () => {
        document.removeEventListener("ionBackButton", handleIonBack);
      };
    }
  }, [isFullscreen, closeFullscreen]);

  if (loading && !isFullscreen) {
    return (
      <div
        className={`insights-card bg-white shadow-md rounded-xl ${className} flex items-center justify-center h-[320px]`}
      >
        <IonSpinner name="crescent" className="text-[#00A3E0]" />
      </div>
    );
  }

  // ── Info panel (matches TherapySetVsDelivered responsive 3-box layout) ──────
  const renderInfoPanel = () => {
    const responsive = getResponsiveLayout(window.innerWidth);
    const isVitals = !showPressures;

    // ── Gather display data ──
    let date: string | null = null;
    let sessionCount: number | null = null;
    let modes: string[] = [];
    let durPep: number | null = null;
    let durOsc: number | null = null;
    let durNeb: number | null = null;
    let pepStat: { min: number; max: number; median: number } | null = null;
    let oscStat: { min: number; max: number; median: number } | null = null;
    let vitalStat: { min: number; max: number; median: number } | null = null;

    if (isInDrillDown) {
      date = drillDownDate ? formatFullDate(drillDownDate, false) : null;
      sessionCount = daySessionsData.length;
      if (activeSessionDetail) {
        modes = activeSessionDetail.mode ? [activeSessionDetail.mode] : [];
        durPep = activeSessionDetail.pepDuration;
        durOsc = activeSessionDetail.oscDuration;
        durNeb = activeSessionDetail.nebDuration;
        pepStat = activeSessionDetail.pepPressure;
        oscStat = activeSessionDetail.oscPressure;
      }
    } else if (isMonthDrillDown) {
      date = new Date(monthDrillDown!.date + "-01T00:00:00").toLocaleDateString(
        "en-US",
        { month: "long", year: "numeric" },
      );
      sessionCount = monthDrillDown!.data.length;
      if (activeData) {
        modes = activeData.summary?.modes ?? [];
        durPep = activeData.durations.pep;
        durOsc = activeData.durations.osc;
        durNeb = activeData.durations.neb;
        pepStat = activeData.pepStat;
        oscStat = activeData.oscStat;
        vitalStat = activeData.vitalStat;
      }
    } else if (activeData) {
      date = activeData.date;
      sessionCount = activeData.summary?.sessionCount ?? null;
      modes = activeData.summary?.modes ?? [];
      durPep = activeData.durations.pep;
      durOsc = activeData.durations.osc;
      durNeb = activeData.durations.neb;
      pepStat = activeData.pepStat;
      oscStat = activeData.oscStat;
      vitalStat = activeData.vitalStat;
    }

    // ── Sub-components ──
    const ColorLabel = ({ color, text }: { color: string; text: string }) => (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "3px",
          marginRight: "3px",
        }}
      >
        <span
          style={{
            width: "7px",
            height: "7px",
            borderRadius: "50%",
            backgroundColor: color,
            flexShrink: 0,
          }}
        />
        <span style={{ fontWeight: 600, fontSize: "10px" }}>{text}</span>
      </span>
    );

    const MeasuredLegend = () => (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "16px",
          fontSize: "10px",
          color: "#6b7280",
          marginTop: "6px",
        }}
      >
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          <span style={{ fontSize: "12px", fontWeight: 600 }}>—</span>
          <span>Median</span>
        </span>
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          <span style={{ fontSize: "12px", fontWeight: 600 }}>◆</span>
          <span>Max</span>
        </span>
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}
        >
          <span style={{ fontSize: "12px", fontWeight: 600 }}>◇</span>
          <span>Min</span>
        </span>
      </div>
    );

    const durationsTitle = isInDrillDown
      ? "Duration (min)"
      : "Avg. Duration (min)";
    const measuredTitle = isVitals
      ? currentMetricConfig.label
      : "Pressures (cmH₂O)";

    const sessionInfoContent = (fontSize: string) => (
      <div style={{ fontSize, color: "#1F2937", lineHeight: 1.6 }}>
        <div style={{ marginBottom: "3px" }}>
          <strong>Date:</strong> {date || "—"}
        </div>
        {sessionCount != null && (
          <div style={{ marginBottom: "3px" }}>
            <strong>Sessions:</strong> {sessionCount}
          </div>
        )}
        {modes.length > 0 && (
          <div>
            <strong>Mode:</strong> {modes.join(", ")}
          </div>
        )}
      </div>
    );

    const durationsContent = (fontSize: string, valueSpacing: string) => (
      <div style={{ fontSize, color: "#1F2937", lineHeight: 1.6 }}>
        {durPep != null || durOsc != null || durNeb != null ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "3px",
              }}
            >
              <ColorLabel color={COLORS.pep} text="PEP:" />
              <span style={{ marginLeft: valueSpacing }}>
                {durPep != null ? `${durPep.toFixed(1)} min` : "—"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "3px",
              }}
            >
              <ColorLabel color={COLORS.osc} text="OSC:" />
              <span style={{ marginLeft: valueSpacing }}>
                {durOsc != null ? `${durOsc.toFixed(1)} min` : "—"}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <ColorLabel color={COLORS.neb} text="NEB:" />
              <span style={{ marginLeft: valueSpacing }}>
                {durNeb != null ? `${durNeb.toFixed(1)} min` : "—"}
              </span>
            </div>
          </>
        ) : (
          <span style={{ color: "#9ca3af" }}>
            {isInDrillDown ? "Select a session" : "—"}
          </span>
        )}
      </div>
    );

    const measuredContent = (fontSize: string, valueSpacing: string) => (
      <div style={{ fontSize, color: "#1F2937", lineHeight: 1.6 }}>
        {isVitals ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            <ColorLabel
              color={vitalColor as string}
              text={`${currentMetricConfig.label}:`}
            />
            <span style={{ marginLeft: valueSpacing }}>
              {vitalStat
                ? `${vitalStat.median.toFixed(1)} (${vitalStat.min.toFixed(
                  1,
                )}–${vitalStat.max.toFixed(1)}) ${currentMetricConfig.unit}`
                : "—"}
            </span>
          </div>
        ) : (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "3px",
              }}
            >
              <ColorLabel color={PRESSURE_PEP_COLOR} text="PEP:" />
              <span style={{ marginLeft: valueSpacing }}>
                {pepStat
                  ? `${pepStat.median.toFixed(1)} (${pepStat.min.toFixed(
                    1,
                  )}–${pepStat.max.toFixed(1)}) cmH₂O`
                  : "—"}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <ColorLabel color={PRESSURE_OSC_COLOR} text="OSC:" />
              <span style={{ marginLeft: valueSpacing }}>
                {oscStat
                  ? `${oscStat.median.toFixed(1)} (${oscStat.min.toFixed(
                    1,
                  )}–${oscStat.max.toFixed(1)}) cmH₂O`
                  : "—"}
              </span>
            </div>
          </>
        )}
      </div>
    );

    // ── Mobile layout (<600px) ──
    if (responsive.layout === "mobile") {
      return (
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              width: "80%",
              background: "#F3F4F6",
              padding: "12px 14px",
              borderRadius: "10px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              boxSizing: "border-box",
            }}
          >
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "10px",
                  color: "#374151",
                  marginBottom: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Session Info
              </div>
              {sessionInfoContent(responsive.fontSize)}
            </div>
            <div style={{ height: "1px", background: "#D1D5DB" }} />
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "10px",
                  color: "#374151",
                  marginBottom: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {durationsTitle}
              </div>
              {durationsContent(responsive.fontSize, responsive.valueSpacing)}
            </div>
            <div style={{ height: "1px", background: "#D1D5DB" }} />
            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: "10px",
                  color: "#374151",
                  marginBottom: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                {measuredTitle}
              </div>
              {measuredContent(responsive.fontSize, responsive.valueSpacing)}
            </div>
          </div>
          {!isVitals && <MeasuredLegend />}
        </div>
      );
    }

    // ── Desktop / landscape / portrait layout (≥600px) ──
    const r = responsive as {
      boxWidth: string;
      gapBetweenBoxes: number;
      boxHeight: string;
      fontSize: string;
      valueSpacing: string;
    };
    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Box 1: Session Info */}
          <div
            style={{
              flex: `0 0 ${r.boxWidth}`,
              background: "#F3F4F6",
              padding: "12px 14px",
              borderRadius: "8px",
              height: r.boxHeight,
              boxSizing: "border-box",
              marginRight: `${r.gapBetweenBoxes}px`,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: "10px",
                color: "#374151",
                marginBottom: "6px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Session Info
            </div>
            {sessionInfoContent(r.fontSize)}
          </div>
          {/* Box 2: Durations */}
          <div
            style={{
              flex: `0 0 ${r.boxWidth}`,
              background: "#F3F4F6",
              padding: "12px 14px",
              borderRadius: "8px",
              height: r.boxHeight,
              boxSizing: "border-box",
              marginRight: `${r.gapBetweenBoxes}px`,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: "10px",
                color: "#374151",
                marginBottom: "6px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {durationsTitle}
            </div>
            {durationsContent(r.fontSize, r.valueSpacing)}
          </div>
          {/* Box 3: Pressures or Vitals */}
          <div
            style={{
              flex: `0 0 ${r.boxWidth}`,
              background: "#F3F4F6",
              padding: "12px 14px",
              borderRadius: "8px",
              height: r.boxHeight,
              boxSizing: "border-box",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: "10px",
                color: "#374151",
                marginBottom: "6px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {measuredTitle}
            </div>
            {measuredContent(r.fontSize, r.valueSpacing)}
          </div>
        </div>
        {!isVitals && <MeasuredLegend />}
      </div>
    );
  };

  return (
    <>
      <div
        className={`insights-card bg-white shadow-md rounded-xl ${className}`}
      >
        {/* Header */}
        <div className="!p-4 biwaze-summary-title">
          <div
            className="patient-avatar-name"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <h2 className="patient-name">{displayTitle}</h2>
            <div className="flex items-center gap-1 shrink-0">
              <button
                aria-label="Fullscreen"
                onClick={openFullscreen}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  style={{ color: "#9ca3af" }}
                >
                  <path d="M20.8346 9.1665H15.8346V10.8332H19.168V14.1665H20.8346V9.1665ZM14.168 19.1665H10.8346V15.8332H9.16797V20.8332H14.168V19.1665Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Info Panel */}
        <div className="px-4 mb-2">{renderInfoPanel()}</div>

        {/* Timeline Navigation */}
        <div style={{ margin: "0 16px 8px 16px" }}>
          <TimelineSlider
            aggregationLevel={
              isInDrillDown || isMonthDrillDown
                ? "daily"
                : (aggregationLevel as any)
            }
            currentContext={
              isInDrillDown && drillDownDateObj
                ? drillDownDateObj
                : isMonthDrillDown
                  ? new Date(monthDrillDown!.date + "-01T00:00:00")
                  : currentContext
            }
            aggregatedData={
              isInDrillDown
                ? timelineAggregatedData
                : isMonthDrillDown
                  ? computedYearMarkers
                  : timelineAggregatedData
            }
            selectedIndex={
              isInDrillDown || isMonthDrillDown ? null : activeIndex
            }
            onItemClick={
              isInDrillDown
                ? handleDrillDownTimelineItemClick
                : isMonthDrillDown
                  ? (item: any, index: number) => {
                    const clickedDate = new Date(item.timestamp);
                    const clickedMonthStr = `${clickedDate.getFullYear()}-${String(
                      clickedDate.getMonth() + 1,
                    ).padStart(2, "0")}`;
                    if (clickedMonthStr !== monthDrillDown!.date) {
                      // Navigate to the different month — updates both chart and timeline
                      fetchMonthDrillDown(clickedMonthStr);
                    } else {
                      handleMonthDrillDownTimelineItemClick(item, index);
                    }
                  }
                  : handleTimelineItemClick
            }
            selectedDay={
              isInDrillDown
                ? drillDownDateObj ?? undefined
                : selectedDay ?? undefined
            }
            selectedMonth={
              isInDrillDown || isMonthDrillDown ? null : selectedMonth
            }
            dataDateRange={
              isMonthDrillDown
                ? timelineDataRange
                : isInDrillDown
                  ? {
                    min: new Date(displayDateRange.startDate + "T00:00:00"),
                    max: new Date(displayDateRange.endDate + "T00:00:00"),
                  }
                  : timelineDataRange
            }
            colors={{
              primary: "#1FB7E3",
              border: "#e0e0e0",
              disabled: "#f0f0f0",
              text: "#666",
              textDisabled: "#ccc",
            }}
            onWheelZoom={handleTimelineWheelZoom}
            onTimelineScroll={handleTimelineScroll}
          />
        </div>

        {/* Axis label row with metric dropdown (hidden during session drill-down) */}
        {!isInDrillDown && (
          <div className="flex justify-between items-center px-4 mb-1">
            <span className="text-sm text-gray-500">Duration (min)</span>
            <div className="inline-flex flex-col items-center leading-tight">
              <CustomDropdown
                options={METRIC_OPTIONS}
                selected={selectedMetric}
                onChange={(v) => setSelectedMetric(v as any)}
                minWidth={120}
                className="text-xs"
              />
              <span className="text-sm text-gray-500">
                {currentMetricConfig.unit}
              </span>
            </div>
          </div>
        )}

        {/* Pressure inline legend (hidden during session drill-down) */}
        {!isInDrillDown && showPressures && (
          <div className="flex gap-6 px-4 mb-1">
            {[
              { label: "Peak OSC Pressure", color: PRESSURE_OSC_COLOR },
              { label: "Peak PEP Pressure", color: PRESSURE_PEP_COLOR },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <svg width="24" height="14" viewBox="0 0 28 14">
                  <line
                    x1="2"
                    y1="7"
                    x2="26"
                    y2="7"
                    stroke={item.color}
                    strokeWidth="1.5"
                    strokeDasharray="5,3"
                  />
                  <path d="M 14,3 L 18,7 L 14,11 L 10,7 Z" fill={item.color} />
                </svg>
                <span className="text-xs text-gray-600">{item.label}</span>
              </div>
            ))}
          </div>
        )}

        {/* Chart area — hidden when fullscreen (shown in IonModal instead) */}
        {!isFullscreen &&
          ((isInDrillDown && daySessionsLoading) || monthDrillDownLoading ? (
            <div
              className="flex items-center justify-center"
              style={{ height: isLargeScreen ? 400 : 280 }}
            >
              <IonSpinner name="crescent" className="text-[#00A3E0]" />
            </div>
          ) : !loading && (fetchError || chartData.length === 0) ? (
            <div
              className="flex flex-col items-center justify-center text-center"
              style={{ height: isLargeScreen ? 400 : 280 }}
            >
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" className="mb-3">
                <circle cx="20" cy="20" r="18" stroke="#E5E7EB" strokeWidth="2" />
                <path d="M14 26 Q20 14 26 26" stroke="#D1D5DB" strokeWidth="2" strokeLinecap="round" fill="none" />
                <circle cx="15" cy="18" r="2" fill="#D1D5DB" />
                <circle cx="25" cy="18" r="2" fill="#D1D5DB" />
              </svg>
              <p className="text-sm font-medium text-gray-500">
                {fetchError ? "Could not load Clear data" : "No Clear data available"}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {fetchError ? "Please check your connection and try again" : "Data will appear once therapy sessions are recorded"}
              </p>
            </div>
          ) : (
            <ParentSize>
              {({ width }) => (
                <ClearPressureVisxChart
                  width={width}
                  height={isLargeScreen ? 400 : 280}
                  chartData={activeChartData}
                  pepStats={activePepStats}
                  oscStats={activeOscStats}
                  showPressures={showPressures}
                  metricStats={activeMetricStats}
                  metricColor={vitalColor}
                  maxDuration={activeMaxDuration}
                  metricRange={metricRange}
                  activeIndex={activeIndex}
                  setActiveIndex={handleActiveIndexChange}
                  onBarClick={(idx) => {
                    const raw = activeChartData[idx]?.date;
                    if (!raw) return;
                    if (isMonthDrillDown) {
                      fetchDaySessionsData(raw);
                    } else if (timePeriod === "Year") {
                      fetchMonthDrillDown(raw.substring(0, 7));
                    } else {
                      fetchDaySessionsData(raw);
                    }
                  }}
                  onSwipeLeft={handleChartSwipeLeft}
                  onSwipeRight={handleChartSwipeRight}
                  onZoomIn={handleChartZoomIn}
                  onZoomOut={handleChartZoomOut}
                  onPan={handleChartPan}
                  onPanEnd={resetDragPan}
                  viewportDates={activeViewport ?? undefined}
                  contextLabel={effectiveContextLabel}
                  metricUnit={currentMetricConfig.unit}
                  sessions={isInDrillDown ? daySessionsData : undefined}
                  activeSessionRefId={
                    isInDrillDown ? activeSessionRefId : undefined
                  }
                  onSessionClick={
                    isInDrillDown ? setActiveSessionRefId : undefined
                  }
                />
              )}
            </ParentSize>
          ))}
      </div>

      {/* ── Fullscreen Modal ─────────────────────────────────────────────────── */}
      <IonModal
        isOpen={isFullscreen}
        onDidDismiss={closeFullscreen}
        showBackdrop={false}
        backdropDismiss={false}
        className="fullscreen-page-modal"
        style={
          {
        "--width": "100vw",
        "--height": "100dvh",
        "--max-height": "100dvh",
        "--border-radius": "0",
        "--background": "#fff",
          } as React.CSSProperties
        }
      >
        <div
          ref={modalRef}
          style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        background: "#fff",
        position: "relative",
        overflow: "hidden",
          }}
        >
          {/* Top bar with close button */}
          <div
        style={{
          flexShrink: 0,
          position: "sticky",
          top: 0,
          zIndex: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: isMobilePhone ? "52px" : "auto",
          paddingTop: isMobilePhone ? "10px" : "calc(env(safe-area-inset-top) + 10px)",
          paddingRight: "calc(env(safe-area-inset-right) + 16px)",
          paddingBottom: "10px",
          paddingLeft: "calc(env(safe-area-inset-left) + 16px)",
          borderBottom: "1px solid #eee",
          background: "#fff",
          boxShadow: "0 1px 0 rgba(0,0,0,0.03)",
        }}
          >
        <button
          onClick={closeFullscreen}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            padding: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
          aria-label="Close fullscreen"
        >
          <IonIcon icon={close} style={{ fontSize: 24, color: "#333" }} />
        </button>

        <span
          style={{
            fontWeight: 600,
            fontSize: 14,
            color: "#1F2937",
            textAlign: "center",
            flex: 1,
            padding: "0 8px",
          }}
        >
          {displayTitle}
        </span>

        {/* Metric dropdown visible in fullscreen */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: isInDrillDown ? 40 : 140,
            justifyContent: "flex-end",
          }}
        >
          {!isInDrillDown ? (
            <>
          <CustomDropdown
            options={METRIC_OPTIONS}
            selected={selectedMetric}
            onChange={(v) => setSelectedMetric(v as any)}
            minWidth={110}
            className="text-xs"
          />
          <span style={{ fontSize: 12, color: "#6b7280" }}>
            ({currentMetricConfig.unit})
          </span>
            </>
          ) : (
            <div style={{ width: 40 }} />
          )}
        </div>
          </div>

          {/* Scrollable content */}
          <div
        style={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch" as any,
        }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                padding: "8px 0 28px 0",
                boxSizing: "border-box",
              }}
            >
              {/* Timeline */}
              <div style={{ margin: "0 16px 8px 16px" }}>
                <TimelineSlider
                  aggregationLevel={
                    isInDrillDown || isMonthDrillDown
                      ? "daily"
                      : (aggregationLevel as any)
                  }
                  currentContext={
                    isInDrillDown && drillDownDateObj
                      ? drillDownDateObj
                      : isMonthDrillDown
                        ? new Date(monthDrillDown!.date + "-01T00:00:00")
                        : currentContext
                  }
                  aggregatedData={
                    isInDrillDown
                      ? timelineAggregatedData
                      : isMonthDrillDown
                        ? computedYearMarkers
                        : timelineAggregatedData
                  }
                  selectedIndex={
                    isInDrillDown || isMonthDrillDown ? null : activeIndex
                  }
                  onItemClick={
                    isInDrillDown
                      ? handleDrillDownTimelineItemClick
                      : isMonthDrillDown
                        ? (item: any, index: number) => {
                          const clickedDate = new Date(item.timestamp);
                          const clickedMonthStr = `${clickedDate.getFullYear()}-${String(
                            clickedDate.getMonth() + 1,
                          ).padStart(2, "0")}`;
                          if (clickedMonthStr !== monthDrillDown!.date) {
                            // Navigate to the different month — updates both chart and timeline
                            fetchMonthDrillDown(clickedMonthStr);
                          } else {
                            handleMonthDrillDownTimelineItemClick(item, index);
                          }
                        }
                        : handleTimelineItemClick
                  }
                  selectedDay={
                    isInDrillDown
                      ? drillDownDateObj ?? undefined
                      : selectedDay ?? undefined
                  }
                  selectedMonth={
                    isInDrillDown || isMonthDrillDown ? null : selectedMonth
                  }
                  dataDateRange={
                    isMonthDrillDown
                      ? timelineDataRange
                      : isInDrillDown
                        ? {
                          min: new Date(
                            displayDateRange.startDate + "T00:00:00",
                          ),
                          max: new Date(displayDateRange.endDate + "T00:00:00"),
                        }
                        : timelineDataRange
                  }
                  colors={{
                    primary: "#1FB7E3",
                    border: "#e0e0e0",
                    disabled: "#f0f0f0",
                    text: "#666",
                    textDisabled: "#ccc",
                  }}
                  onWheelZoom={handleTimelineWheelZoom}
                  onTimelineScroll={handleTimelineScroll}
                />
              </div>

              {/* Pressure inline legend */}
              {!isInDrillDown && showPressures && (
                <div className="flex gap-6 px-4 mb-1">
                  {[
                    { label: "Peak OSC Pressure", color: PRESSURE_OSC_COLOR },
                    { label: "Peak PEP Pressure", color: PRESSURE_PEP_COLOR },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <svg width="24" height="14" viewBox="0 0 28 14">
                        <line
                          x1="2"
                          y1="7"
                          x2="26"
                          y2="7"
                          stroke={item.color}
                          strokeWidth="1.5"
                          strokeDasharray="5,3"
                        />
                        <path
                          d="M 14,3 L 18,7 L 14,11 L 10,7 Z"
                          fill={item.color}
                        />
                      </svg>
                      <span className="text-xs text-gray-600">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
                {!isInDrillDown && (
                  <div className="flex justify-between items-center px-4 mb-1">
                    <span className="text-sm text-gray-500">Duration (min)</span>
                    <div className="inline-flex flex-col items-center leading-tight">
                      <CustomDropdown
                        options={METRIC_OPTIONS}
                        selected={selectedMetric}
                        onChange={(v) => setSelectedMetric(v as any)}
                        minWidth={120}
                        className="text-xs"
                      />
                      <span className="text-sm text-gray-500">
                        {currentMetricConfig.unit}
                      </span>
                    </div>
                  </div>
                )}
              
              <div className="px-4 mb-2">{renderInfoPanel()}</div>

              {/* Chart */}
              <div
                style={{
                  width: "100%",
                  height: "70vh",
                  overflow: "visible",
                  marginTop: 4,
                  paddingLeft: window.matchMedia(
                    "(orientation: landscape) and (max-width: 926px)",
                  ).matches
                    ? 24
                    : 0,
                }}
              >
                {loading || (isInDrillDown && daySessionsLoading) ||
                  monthDrillDownLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <IonSpinner name="crescent" className="text-[#00A3E0]" />
                  </div>
                ) : (
                  <ParentSize>
                    {({ width, height }) => (
                      <ClearPressureVisxChart
                        width={width}
                        height={height}
                        chartData={activeChartData}
                        pepStats={activePepStats}
                        oscStats={activeOscStats}
                        showPressures={showPressures}
                        metricStats={activeMetricStats}
                        metricColor={vitalColor}
                        maxDuration={activeMaxDuration}
                        metricRange={metricRange}
                        activeIndex={activeIndex}
                        setActiveIndex={handleActiveIndexChange}
                        onBarClick={(idx) => {
                          const raw = activeChartData[idx]?.date;
                          if (!raw) return;
                          if (isMonthDrillDown) {
                            fetchDaySessionsData(raw);
                          } else if (timePeriod === "Year") {
                            fetchMonthDrillDown(raw.substring(0, 7));
                          } else {
                            fetchDaySessionsData(raw);
                          }
                        }}
                        onSwipeLeft={handleChartSwipeLeft}
                        onSwipeRight={handleChartSwipeRight}
                        onZoomIn={handleChartZoomIn}
                        onZoomOut={handleChartZoomOut}
                        onPan={handleChartPan}
                        onPanEnd={resetDragPan}
                        viewportDates={activeViewport ?? undefined}
                        contextLabel={effectiveContextLabel}
                        metricUnit={currentMetricConfig.unit}
                        sessions={isInDrillDown ? daySessionsData : undefined}
                        activeSessionRefId={
                          isInDrillDown ? activeSessionRefId : undefined
                        }
                        onSessionClick={
                          isInDrillDown ? setActiveSessionRefId : undefined
                        }
                      />
                    )}
                  </ParentSize>
                )}
              </div>
            </div>
          </div>
        </div>
      </IonModal>
    </>
  );
};

// ── Chart sub-component (same SVG chart as ClearTherapyPressureGraph) ─────────
interface ClearPressureVisxChartProps {
  width: number;
  height: number;
  chartData: ChartDataPoint[];
  pepStats: PressureStat[];
  oscStats: PressureStat[];
  showPressures: boolean;
  metricStats: PressureStat[];
  metricColor: string;
  maxDuration: number;
  metricRange: [number, number];
  activeIndex: number | null;
  setActiveIndex: (i: number | null) => void;
  onBarClick?: (idx: number) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onZoomIn?: (anchorIdx?: number) => void;
  onZoomOut?: (anchorIdx?: number) => void;
  onPan?: (deltaX: number, chartWidth: number) => void;
  onPanEnd?: () => void;
  contextLabel?: string;
  metricUnit?: string;
  viewportDates?: { start: Date; end: Date };
  // Session drill-down mode
  sessions?: ClearTherapySessionDetail[];
  activeSessionRefId?: string | null;
  onSessionClick?: (refId: string) => void;
}

const ClearPressureVisxChart: React.FC<ClearPressureVisxChartProps> = ({
  width,
  height,
  chartData,
  pepStats,
  oscStats,
  showPressures,
  metricStats,
  metricColor,
  maxDuration,
  metricRange,
  activeIndex,
  setActiveIndex,
  onBarClick,
  onSwipeLeft,
  onSwipeRight,
  onZoomIn,
  onZoomOut,
  onPan,
  onPanEnd,
  contextLabel,
  metricUnit,
  viewportDates,
  sessions,
  activeSessionRefId,
  onSessionClick,
}) => {
  const touchStartXRef = useRef<number | null>(null);
  const margin = { top: 20, right: 55, bottom: 60, left: 50 };
  const xMax = width - margin.left - margin.right;
  const yMax = height - margin.top - margin.bottom;

  // ── Session drill-down mode ──────────────────────────────────────────────
  // When sessions are provided the chart switches to a session-level view with
  // scaleBand categorical bars (one bar per session) instead of scaleTime.
  if (sessions) {
    const sessionLabels = sessions.map((s, i) => getSessionLabel(s, i));
    const sxScale = scaleBand({
      domain: sessionLabels,
      range: [0, xMax],
      padding: 0.28,
    });

    const sessionMaxDuration = Math.max(
      5,
      Math.ceil(
        (Math.max(
          ...sessions.map((s) => s.pepDuration + s.oscDuration + s.nebDuration),
        ) *
          1.25) /
        5,
      ) * 5,
    );
    const syDuration = scaleLinear({
      domain: [0, sessionMaxDuration],
      range: [yMax, 0],
    });

    const sessionPressureVals = sessions
      .flatMap((s) => [
        s.pepPressure?.median ?? 0,
        s.oscPressure?.median ?? 0,
      ])
      .filter((v) => v > 0);
    const sessionMaxPressure = sessionPressureVals.length
      ? Math.ceil((Math.max(...sessionPressureVals) * 1.25) / 5) * 5
      : 30;
    const sessionMinPressure = sessionPressureVals.length
      ? Math.floor((Math.min(...sessionPressureVals) * 0.75) / 5) * 5
      : 0;
    const sessionDataRange = sessionMaxPressure - sessionMinPressure;
    const syCenter = sessionMinPressure;
    const syPressure = scaleLinear({
      domain: [syCenter - sessionDataRange, sessionMaxPressure],
      range: [yMax, 0],
      nice: true,
    });

    const sDurationTicks = (() => {
      const step =
        sessionMaxDuration <= 10 ? 1 : sessionMaxDuration <= 30 ? 5 : 10;
      return Array.from(
        { length: Math.floor(sessionMaxDuration / step) + 1 },
        (_, i) => i * step,
      );
    })();
    const sPressureTicks = syPressure.ticks(5).map((t) => Math.round(t));

    const pepLine = sessions
      .map((s, i) =>
        s.pepPressure ? { x: sessionLabels[i], y: s.pepPressure.median } : null,
      )
      .filter((p): p is { x: string; y: number } => p !== null);
    const oscLine = sessions
      .map((s, i) =>
        s.oscPressure ? { x: sessionLabels[i], y: s.oscPressure.median } : null,
      )
      .filter((p): p is { x: string; y: number } => p !== null);

    const sDiamond = (cx: number, cy: number, size: number) =>
      `M ${cx},${cy - size} L ${cx + size},${cy} L ${cx},${cy + size} L ${
        cx - size
      },${cy} Z`;

    const PEP_NEB_DOT_ID_S = "clear-session-pep-neb-dots";
    const OSC_NEB_DOT_ID_S = "clear-session-osc-neb-dots";

    return (
      <div style={{ position: "relative", width, height }}>
        {sessions.length === 0 && (
          <div className="absolute inset-0 flex flex-col  justify-center text-center pointer-events-none">
            <p className="text-sm font-medium text-gray-500">No therapy sessions</p>
            <p className="text-xs text-gray-400 mt-1">
              {contextLabel || ""}
            </p>
            <p className="text-xs text-gray-400 mt-1">Swipe left or right to view other days</p>
          </div>
        )}
        <svg width={width} height={height} style={{ overflow: "visible" }}>
          <defs>
            <pattern
              id={PEP_NEB_DOT_ID_S}
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
            >
              <rect width="6" height="6" fill="transparent" />
              <circle cx="3" cy="3" r="1" fill="#00BCD4" opacity="0.75" />
            </pattern>
            <pattern
              id={OSC_NEB_DOT_ID_S}
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
            >
              <rect width="6" height="6" fill="transparent" />
              <circle cx="3" cy="3" r="1" fill={COLORS.neb} opacity="0.75" />
            </pattern>
          </defs>
          <Group left={margin.left} top={margin.top}>
            {/* Grid */}
            {syDuration.ticks(5).map((tick, i) => (
              <line
                key={i}
                x1={0}
                y1={syDuration(tick)}
                x2={xMax}
                y2={syDuration(tick)}
                stroke={COLORS.grid}
                strokeWidth={1}
              />
            ))}

            {/* Session bars */}
            {sessions.map((s, idx) => {
              const label = sessionLabels[idx];
              const bx = sxScale(label) ?? 0;
              const bw = sxScale.bandwidth();
              const barW = bw * 0.72;
              const barX = bx + (bw - barW) / 2;
              const isActive = activeSessionRefId === s.refId;
              const pepNebDuration = Math.min(
                (s as any).pepWithNebTime ?? 0,
                s.pepDuration,
              );
              const pepNebH = yMax - syDuration(pepNebDuration);
              const oscNebDuration = Math.min(
                (s as any).oscWithNebTime ?? 0,
                s.oscDuration,
              );

              return (
                <g key={s.refId}>
                  {isActive && (
                    <rect
                      x={bx - 1}
                      y={0}
                      width={bw + 2}
                      height={yMax}
                      fill="rgba(0,163,224,0.07)"
                      rx={3}
                    />
                  )}
                  {s.pepDuration > 0 && (
                    <rect
                      x={barX}
                      y={syDuration(s.pepDuration)}
                      width={barW}
                      height={Math.max(yMax - syDuration(s.pepDuration), 0)}
                      fill={COLORS.pep}
                      rx={s.oscDuration === 0 && s.nebDuration === 0 ? 2 : 0}
                    />
                  )}
                  {pepNebDuration > 0 && s.pepDuration > 0 && (
                    <rect
                      x={barX}
                      y={syDuration(pepNebDuration)}
                      width={barW}
                      height={Math.max(pepNebH, 0)}
                      fill={`url(#${PEP_NEB_DOT_ID_S})`}
                      pointerEvents="none"
                    />
                  )}
                  {s.oscDuration > 0 && (
                    <rect
                      x={barX}
                      y={syDuration(s.pepDuration + s.oscDuration)}
                      width={barW}
                      height={Math.max(
                        syDuration(s.pepDuration) -
                        syDuration(s.pepDuration + s.oscDuration),
                        0,
                      )}
                      fill={COLORS.osc}
                      rx={s.nebDuration === 0 ? 2 : 0}
                    />
                  )}
                  {/* OSC+NEB dot overlay — portion of OSC bar where NEB was active */}
                  {oscNebDuration > 0 && s.oscDuration > 0 && (
                    <rect
                      x={barX}
                      y={syDuration(s.pepDuration + oscNebDuration)}
                      width={barW}
                      height={Math.max(
                        syDuration(s.pepDuration) -
                        syDuration(s.pepDuration + oscNebDuration),
                        0,
                      )}
                      fill={`url(#${OSC_NEB_DOT_ID_S})`}
                      pointerEvents="none"
                    />
                  )}
                  {s.nebDuration > 0 && (
                    <rect
                      x={barX}
                      y={syDuration(
                        s.pepDuration + s.oscDuration + s.nebDuration,
                      )}
                      width={barW}
                      height={Math.max(
                        syDuration(s.pepDuration + s.oscDuration) -
                        syDuration(
                          s.pepDuration + s.oscDuration + s.nebDuration,
                        ),
                        0,
                      )}
                      fill={COLORS.neb}
                      rx={2}
                    />
                  )}
                  {isActive && (
                    <line
                      x1={bx + bw / 2}
                      y1={0}
                      x2={bx + bw / 2}
                      y2={yMax}
                      stroke={COLORS.cursor}
                      strokeWidth={2}
                      strokeDasharray="4,4"
                      pointerEvents="none"
                    />
                  )}
                </g>
              );
            })}

            {/* Pressure lines */}
            {pepLine.length > 1 && (
              <LinePath
                data={pepLine}
                x={(d) => (sxScale(d.x) ?? 0) + sxScale.bandwidth() / 2}
                y={(d) => syPressure(d.y)}
                stroke={PRESSURE_PEP_COLOR}
                strokeWidth={1.5}
                strokeDasharray="6,4"
                curve={curveLinear}
              />
            )}
            {oscLine.length > 1 && (
              <LinePath
                data={oscLine}
                x={(d) => (sxScale(d.x) ?? 0) + sxScale.bandwidth() / 2}
                y={(d) => syPressure(d.y)}
                stroke={PRESSURE_OSC_COLOR}
                strokeWidth={1.5}
                strokeDasharray="6,4"
                curve={curveLinear}
              />
            )}

            {/* Pressure diamonds */}
            {sessions.map((s, idx) => {
              const scx =
                (sxScale(sessionLabels[idx]) ?? 0) + sxScale.bandwidth() / 2;
              return (
                <g key={`pres-${s.refId}`} pointerEvents="none">
                  {s.pepPressure && (
                    <path
                      d={sDiamond(scx, syPressure(s.pepPressure.median), 5)}
                      fill={PRESSURE_PEP_COLOR}
                      stroke="white"
                      strokeWidth={1}
                    />
                  )}
                  {s.oscPressure && (
                    <path
                      d={sDiamond(scx, syPressure(s.oscPressure.median), 5)}
                      fill={PRESSURE_OSC_COLOR}
                      stroke="white"
                      strokeWidth={1}
                    />
                  )}
                </g>
              );
            })}

            {/* Swipe + tap overlay — full touch handling mirrors Month/Year branch */}
            <rect
              width={xMax}
              height={yMax}
              fill="transparent"
              pointerEvents="all"
              style={{ touchAction: "none", cursor: "pointer" }}
              onTouchStart={(e) => {
                e.stopPropagation();
                const clientX = e.touches[0].clientX;
                touchStartXRef.current = clientX;
                // Map touch X to nearest session → show cursor immediately on tap
                const rect = (e.currentTarget as Element).getBoundingClientRect();
                const x = clientX - rect.left;
                let nearestIdx = 0;
                let minDist = Infinity;
                sessionLabels.forEach((label, idx) => {
                  const bandX = (sxScale(label) ?? 0) + sxScale.bandwidth() / 2;
                  const dist = Math.abs(bandX - x);
                  if (dist < minDist) { minDist = dist; nearestIdx = idx; }
                });
                if (sessions[nearestIdx]) onSessionClick?.(sessions[nearestIdx].refId);
              }}
              onTouchMove={(e) => {
                // Only block browser scroll — do NOT update activeSessionRefId here.
                // If we update it during a swipe the finger passes over earlier sessions,
                // causing handleChartSwipeLeft/Right to navigate to the wrong session
                // instead of exiting to the next/previous day.
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
                if (e.changedTouches.length === 1) {
                  const startX = touchStartXRef.current ?? e.changedTouches[0].clientX;
                  const dx = e.changedTouches[0].clientX - startX;
                  if (Math.abs(dx) > 50) {
                    if (dx < 0) onSwipeLeft?.();
                    else onSwipeRight?.();
                  }
                }
              }}
              onClick={(e) => {
                const rect = (e.currentTarget as Element).getBoundingClientRect();
                const x = e.clientX - rect.left;
                let nearestIdx = 0;
                let minDist = Infinity;
                sessionLabels.forEach((label, idx) => {
                  const bandX = (sxScale(label) ?? 0) + sxScale.bandwidth() / 2;
                  const dist = Math.abs(bandX - x);
                  if (dist < minDist) { minDist = dist; nearestIdx = idx; }
                });
                if (sessions[nearestIdx]) onSessionClick?.(sessions[nearestIdx].refId);
              }}
            />

            {/* Axes */}
            <AxisBottom
              top={yMax}
              scale={sxScale}
              stroke="#9ca3af"
              tickStroke="#9ca3af"
              tickLength={0}
              tickLabelProps={() => ({
                fill: "#666",
                fontSize: 10,
                textAnchor: "middle",
                dy: 4,
              })}
            />
            <AxisLeft
              scale={syDuration}
              tickValues={sDurationTicks}
              stroke="#9ca3af"
              tickStroke="#9ca3af"
              tickLength={0}
              tickLabelProps={() => ({
                fill: "#666",
                fontSize: 10,
                textAnchor: "end",
                dx: -6,
              })}
              label="min"
              labelProps={{
                fill: "#9ca3af",
                fontSize: 10,
                textAnchor: "middle",
                dx: 6,
                dy: -4,
              }}
            />
            <AxisRight
              left={xMax}
              scale={syPressure}
              tickValues={sPressureTicks}
              tickFormat={(v) => String(Math.round(Number(v)))}
              stroke="#9ca3af"
              tickStroke="#9ca3af"
              tickLength={0}
              tickLabelProps={(v) => ({
                fill: Number(v) < syCenter ? "#d1d5db" : "#111",
                fontSize: 10,
                textAnchor: "start",
                dx: 8,
              })}
              label="cmH₂O"
              labelProps={{
                fill: "#9ca3af",
                fontSize: 10,
                textAnchor: "middle",
                dx: -6,
                dy: 4,
              }}
            />
            {/* Context label — date being inspected */}
            {contextLabel && (
              <text
                x={xMax / 2}
                y={yMax + 50}
                fontSize={13}
                fontWeight={600}
                fill="#374151"
                textAnchor="middle"
              >
                {contextLabel}
              </text>
            )}
          </Group>
        </svg>
      </div>
    );
  }

  // ── X scale: continuous time axis ─────────────────────────────────────────
  const firstDate =
    chartData.length > 0
      ? new Date(normalizeDateKey(chartData[0].date) + "T00:00:00")
      : new Date();
  const lastDate =
    chartData.length > 0
      ? new Date(
        normalizeDateKey(chartData[chartData.length - 1].date) + "T23:59:59",
      )
      : new Date();

  const xDomainStart = viewportDates?.start ?? firstDate;
  const xDomainEnd = viewportDates?.end ?? lastDate;

  // Bar width: proportional to one time-unit (day or month).
  // Computed BEFORE the scale so we can apply symmetric padding to the domain.
  const msPerUnit =
    chartData.length > 1
      ? new Date(normalizeDateKey(chartData[1].date)).getTime() -
      new Date(normalizeDateKey(chartData[0].date)).getTime()
      : 24 * 60 * 60 * 1000;

  // Extend the domain by half a time-unit on each side so that bars at the
  // first and last positions are centred in their slot, not clipped by the axes.
  const halfUnit = msPerUnit / 2;
  const xScalePaddedStart = new Date(xDomainStart.getTime() - halfUnit);
  const xScalePaddedEnd = new Date(xDomainEnd.getTime() + halfUnit);

  const xScale = scaleTime({
    domain: [xScalePaddedStart, xScalePaddedEnd],
    range: [0, xMax],
  });

  const pixelsPerUnit =
    msPerUnit * (xMax / (xScalePaddedEnd.getTime() - xScalePaddedStart.getTime() || 1));
  const barWidthPx = Math.max(4, pixelsPerUnit * 0.7);

  // Helper: is the date range yearly (>40 days)?
  const isYearView =
    xDomainEnd.getTime() - xDomainStart.getTime() > 40 * 24 * 60 * 60 * 1000;
  const MONTH_LABELS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const durationMax = Math.max(5, Math.ceil((maxDuration * 1.15) / 5) * 5);
  const yDuration = scaleLinear({
    domain: [0, durationMax],
    range: [yMax, 0],
  });

  // Negative-axis technique: center = metricRange[0] (actual data minimum, e.g. 83 for SpO2,
  // 0 for pressure). The upper half shows real data [center → metricRange[1]].
  // The lower half mirrors the same span downward [center - dataRange → center],
  // so the axis always continues below with the same step spacing.
  const yCenter = metricRange[0];
  const dataRange = metricRange[1] - metricRange[0];
  const yPressure = scaleLinear({
    domain: [yCenter - dataRange, metricRange[1]],
    range: [yMax, 0],
    nice: true,
  });

  const minTickSpacingPx = 20;
  const maxFittingTicks = Math.floor(yMax / minTickSpacingPx);
  const tickInterval =
    durationMax <= maxFittingTicks
      ? 1
      : Math.ceil(durationMax / 5) <= maxFittingTicks
        ? 5
        : Math.ceil(durationMax / 10) <= maxFittingTicks
          ? 10
          : 20;
  const durationTicks = Array.from(
    { length: Math.floor(durationMax / tickInterval) + 1 },
    (_, i) => i * tickInterval,
  );
  const pressureTicks = yPressure.ticks(5).map((t) => Math.round(t));

  const svgRef = useRef<SVGSVGElement>(null);

  const SWIPE_THRESHOLD = 50;
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{ startX: number; moved: boolean } | null>(null);
  const pinchRef = useRef<{
    initialDist: number;
    anchorX: number;
    hasZoomed: boolean;
  } | null>(null);
  const wheelRef = useRef(0);

  // Map a clientX pixel to the nearest chartData index using scaleTime invert
  const getNearestIdxFromClientX = (clientX: number): number => {
    const svgEl = svgRef.current;
    if (!svgEl || chartData.length === 0) return 0;
    const rect = svgEl.getBoundingClientRect();
    const x = clientX - rect.left - margin.left;
    const targetDate = (xScale as any).invert(
      Math.max(0, Math.min(xMax, x)),
    ) as Date;
    const targetTime = targetDate.getTime();
    let nearestIdx = 0;
    let minDist = Infinity;
    chartData.forEach((d, idx) => {
      const dist = Math.abs(
        new Date(normalizeDateKey(d.date) + "T00:00:00").getTime() - targetTime,
      );
      if (dist < minDist) {
        minDist = dist;
        nearestIdx = idx;
      }
    });
    return nearestIdx;
  };

  const diamond = (cx: number, cy: number, size: number) =>
    `M ${cx},${cy - size} L ${cx + size},${cy} L ${cx},${cy + size} L ${
      cx - size
    },${cy} Z`;

  const cx = (stat: PressureStat) =>
    xScale(new Date(normalizeDateKey(stat.date) + "T00:00:00"));

  const renderErrorBars = (
    stats: PressureStat[],
    color: string,
    keyPrefix: string,
  ) =>
    stats.map((stat, idx) => {
      const x = cx(stat);
      return (
        <g key={`${keyPrefix}-${idx}`}>
          <Line
            from={{ x, y: yPressure(stat.min) }}
            to={{ x, y: yPressure(stat.max) }}
            stroke={color}
            strokeWidth={1.5}
            strokeDasharray="4,2"
          />
          <Line
            from={{ x: x - 4, y: yPressure(stat.min) }}
            to={{ x: x + 4, y: yPressure(stat.min) }}
            stroke={color}
            strokeWidth={1.5}
          />
          <Line
            from={{ x: x - 4, y: yPressure(stat.max) }}
            to={{ x: x + 4, y: yPressure(stat.max) }}
            stroke={color}
            strokeWidth={1.5}
          />
        </g>
      );
    });

  const renderMedianLine = (stats: PressureStat[], color: string) =>
    stats.length > 0 && (
      <LinePath
        data={stats}
        x={(d) => cx(d)}
        y={(d) => yPressure(d.median)}
        stroke={color}
        strokeWidth={2}
        strokeDasharray="6,4"
        curve={curveLinear}
      />
    );

  const renderMarkers = (
    stats: PressureStat[],
    color: string,
    keyPrefix: string,
  ) =>
    stats.map((stat, idx) => {
      const x = cx(stat);
      const medY = yPressure(stat.median);
      return (
        <g key={`${keyPrefix}-markers-${idx}`}>
          <path
            d={diamond(x, yPressure(stat.max), 5)}
            fill={color}
            stroke="white"
            strokeWidth={0.5}
          />
          <path
            d={diamond(x, yPressure(stat.min), 4)}
            fill="white"
            stroke={color}
            strokeWidth={1.5}
          />
          <line
            x1={x - 6}
            y1={medY}
            x2={x + 6}
            y2={medY}
            stroke={color}
            strokeWidth={2.5}
            strokeLinecap="round"
          />
        </g>
      );
    });

  const PEP_NEB_DOT_ID = "clear-main-pep-neb-dots";
  const OSC_NEB_DOT_ID = "clear-main-osc-neb-dots";
  const hasDataInViewport = useMemo(() => {
    return chartData.some((d) => {
      const ts = new Date(normalizeDateKey(d.date) + "T00:00:00").getTime();
      return (
        ts >= xDomainStart.getTime() &&
        ts <= xDomainEnd.getTime() &&
        (d.avgNebDuration > 0 || d.avgPepDuration > 0 || d.avgOscDuration > 0)
      );
    });
  }, [chartData, xDomainStart, xDomainEnd]);

  return (
    <div style={{ position: "relative" }}>
      <svg ref={svgRef} width={width} height={height}>
        <defs>
          <pattern
            id={PEP_NEB_DOT_ID}
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
          >
            <rect width="6" height="6" fill="transparent" />
            <circle cx="3" cy="3" r="1" fill="#00BCD4" opacity="0.75" />
          </pattern>
          <pattern
            id={OSC_NEB_DOT_ID}
            patternUnits="userSpaceOnUse"
            width="6"
            height="6"
          >
            <rect width="6" height="6" fill="transparent" />
            <circle cx="3" cy="3" r="1" fill={COLORS.neb} opacity="0.75" />
          </pattern>
        </defs>
        <Group left={margin.left} top={margin.top}>
          {/* Grid lines */}
          {yDuration.ticks(5).map((tick, i) => (
            <Line
              key={`grid-${i}`}
              from={{ x: 0, y: yDuration(tick) }}
              to={{ x: xMax, y: yDuration(tick) }}
              stroke={COLORS.grid}
              strokeWidth={1}
            />
          ))}

          {/* Alternating column background */}
          {chartData.map((d, idx) => {
            if (idx % 2 !== 1) return null;
            const bx = xScale(new Date(normalizeDateKey(d.date) + "T00:00:00"));
            return (
              <rect
                key={`bg-${idx}`}
                x={bx - barWidthPx * 0.15}
                y={0}
                width={barWidthPx * 1.3}
                height={yMax}
                fill="rgba(0,0,0,0.03)"
              />
            );
          })}

          {/* Empty state overlay with time axis */}
          {!hasDataInViewport && (
            <Group>
              <rect
                x={0}
                y={0}
                width={xMax}
                height={yMax}
                fill="rgba(255, 255, 255, 0.8)"
              />
              <text
                x={xMax / 2}
                y={yMax / 2 - 20}
                fontSize={14}
                fill="#9ca3af"
                textAnchor="middle"
                fontWeight={500}
              >
                No Therapy Data
              </text>
              <text
                x={xMax / 2}
                y={yMax / 2}
                fontSize={12}
                fill="#9ca3af"
                textAnchor="middle"
              >
                {(() => {
                  if (isYearView) {
                    if (xDomainStart.getFullYear() === xDomainEnd.getFullYear()) {
                      return `${xDomainStart.toLocaleDateString(undefined, {
                        month: "short",
                        year: "numeric",
                      })} - ${xDomainEnd.toLocaleDateString(undefined, {
                        month: "short",
                        year: "numeric",
                      })}`;
                    }
                    return `${xDomainStart.toLocaleDateString(undefined, {
                      year: "numeric",
                    })} - ${xDomainEnd.toLocaleDateString(undefined, {
                      year: "numeric",
                    })}`;
                  } else {
                    const sameDay = xDomainStart.toDateString() === xDomainEnd.toDateString();
                    if (sameDay) {
                      return xDomainStart.toLocaleDateString(undefined, {
                        month: "long",
                        day: "numeric",
                        year: "numeric",
                      });
                    }
                    return `${xDomainStart.toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                    })} - ${xDomainEnd.toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}`;
                  }
                })()}
              </text>
              <text
                x={xMax / 2}
                y={yMax / 2 + 20}
                fontSize={11}
                fill="#9ca3af"
                textAnchor="middle"
              >
                Pan left or right to view other periods
              </text>
            </Group>
          )}

          {/* Stacked bars: PEP (bottom) → OSC (middle) → NEB (top) */}
          {hasDataInViewport && chartData.map((d, idx) => {
            const bx = xScale(new Date(normalizeDateKey(d.date) + "T00:00:00"));
            const barW = barWidthPx;
            const barX = bx - barW / 2;
            const {
              avgNebDuration: neb,
              avgPepDuration: pep,
              avgOscDuration: osc,
            } = d;

            // avgPepWithNebTime: prefer field directly on the point, fall back to
            // deriving from summary totals (for API responses that aggregate only).
            const sessionCount = d.summary?.sessionCount || 1;
            const pepWithNeb = Math.min(
              d.avgPepWithNebTime ??
              (d.summary?.totalPepWithNebTime != null
                ? d.summary.totalPepWithNebTime / sessionCount
                : 0),
              pep,
            );
            const oscWithNeb = Math.min(
              d.avgOscWithNebTime ??
              (d.summary?.totalOscWithNebTime != null
                ? d.summary.totalOscWithNebTime / sessionCount
                : 0),
              osc,
            );

            const pepY = yDuration(pep);
            const pepH = yMax - pepY;
            const oscY = yDuration(pep + osc);
            const oscH = yDuration(pep) - oscY;
            const nebY = yDuration(pep + osc + neb);
            const nebH = yDuration(pep + osc) - nebY;

            return (
              <g key={`bar-${idx}`}>
                {pep > 0 && (
                  <rect
                    x={barX}
                    y={pepY}
                    width={barW}
                    height={pepH}
                    fill={COLORS.pep}
                    rx={osc === 0 && neb === 0 ? 2 : 0}
                  />
                )}
                {/* PEP+NEB dot overlay — bottom portion of PEP bar where NEB was active */}
                {pepWithNeb > 0 && pep > 0 && (
                  <rect
                    x={barX}
                    y={yDuration(pepWithNeb)}
                    width={barW}
                    height={Math.max(yMax - yDuration(pepWithNeb), 0)}
                    fill={`url(#${PEP_NEB_DOT_ID})`}
                    pointerEvents="none"
                  />
                )}
                {osc > 0 && (
                  <rect
                    x={barX}
                    y={oscY}
                    width={barW}
                    height={oscH}
                    fill={COLORS.osc}
                    rx={neb === 0 ? 2 : 0}
                  />
                )}
                {/* OSC+NEB dot overlay — bottom portion of OSC bar where NEB was active */}
                {oscWithNeb > 0 && osc > 0 && (
                  <rect
                    x={barX}
                    y={yDuration(pep + oscWithNeb)}
                    width={barW}
                    height={Math.max(yDuration(pep) - yDuration(pep + oscWithNeb), 0)}
                    fill={`url(#${OSC_NEB_DOT_ID})`}
                    pointerEvents="none"
                  />
                )}
                {neb > 0 && (
                  <rect
                    x={barX}
                    y={nebY}
                    width={barW}
                    height={nebH}
                    fill={COLORS.neb}
                    rx={2}
                  />
                )}
              </g>
            );
          })}

          {/* Centre baseline — metricRange[0] on Y2, divides real data (above) from lower fill (below) */}
          <line
            x1={0}
            x2={xMax}
            y1={yPressure(yCenter)}
            y2={yPressure(yCenter)}
            stroke="#d1d5db"
            strokeWidth={1}
            strokeDasharray="4,3"
          />

          {/* Pressure mode: OSC (cyan) + PEP (pink) dual series */}
          {hasDataInViewport && showPressures && (
            <>
              {renderErrorBars(oscStats, PRESSURE_OSC_COLOR, "osc-err")}
              {renderErrorBars(pepStats, PRESSURE_PEP_COLOR, "pep-err")}
              {renderMedianLine(oscStats, PRESSURE_OSC_COLOR)}
              {renderMedianLine(pepStats, PRESSURE_PEP_COLOR)}
              {renderMarkers(oscStats, PRESSURE_OSC_COLOR, "osc")}
              {renderMarkers(pepStats, PRESSURE_PEP_COLOR, "pep")}
            </>
          )}

          {/* Vital mode: single series */}
          {hasDataInViewport && !showPressures && (
            <>
              {renderErrorBars(metricStats, metricColor, "vital-err")}
              {renderMedianLine(metricStats, metricColor)}
              {renderMarkers(metricStats, metricColor, "vital")}
            </>
          )}

          {/* Active cursor */}
          {hasDataInViewport &&
            activeIndex !== null &&
            chartData[activeIndex] &&
            (() => {
              const cursorX = xScale(
                new Date(
                  normalizeDateKey(chartData[activeIndex].date) + "T00:00:00",
                ),
              );
              return (
                <>
                  <Line
                    from={{ x: cursorX, y: 10 }}
                    to={{ x: cursorX, y: yMax }}
                    stroke={COLORS.cursor}
                    strokeWidth={2}
                    strokeDasharray="4,4"
                    pointerEvents="none"
                  />
                  <polygon
                    points={`${cursorX},10 ${cursorX - 7},0 ${cursorX + 7},0`}
                    fill={COLORS.cursor}
                    pointerEvents="none"
                  />
                </>
              );
            })()}

          {/* Interactive overlay with pan support */}
          <rect
            width={xMax}
            height={yMax}
            fill="transparent"
            pointerEvents="all"
            style={{
              cursor: isDragging ? "grabbing" : "grab",
              touchAction: "none",
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              setIsDragging(true);
              dragRef.current = { startX: e.clientX, moved: false };
              setActiveIndex(getNearestIdxFromClientX(e.clientX));
            }}
            onMouseMove={(e) => {
              e.stopPropagation();
              if (dragRef.current) {
                dragRef.current.moved = true;
                onPan?.(e.clientX - dragRef.current.startX, xMax);
              }
              setActiveIndex(getNearestIdxFromClientX(e.clientX));
            }}
            onMouseUp={(e) => {
              e.stopPropagation();
              if (!dragRef.current) return;
              const { startX, moved } = dragRef.current;
              const dx = e.clientX - startX;
              if (Math.abs(dx) > SWIPE_THRESHOLD) {
                if (dx < 0) onSwipeLeft?.();
                else onSwipeRight?.();
              } else if (!moved && onBarClick) {
                onBarClick(getNearestIdxFromClientX(e.clientX));
              }
              dragRef.current = null;
              setIsDragging(false);
              onPanEnd?.();
            }}
            onMouseLeave={() => {
              setIsDragging(false);
              dragRef.current = null;
            }}
            onWheel={(e) => {
              e.preventDefault();
              const now = Date.now();
              // Trackpads emit many tiny wheel events; throttle to one zoom step per gesture burst.
              if (now - wheelRef.current < 120) return;
              const anchorIdx = getNearestIdxFromClientX(e.clientX);
              if (e.deltaY < 0) onZoomIn?.(anchorIdx);
              else if (e.deltaY > 0) onZoomOut?.(anchorIdx);
              wheelRef.current = now;
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
              if (e.touches.length === 2) {
                const t0 = e.touches[0],
                  t1 = e.touches[1];
                const rect = (
                  e.currentTarget as Element
                ).getBoundingClientRect();
                const midClientX = (t0.clientX + t1.clientX) / 2;
                const anchorX = midClientX - rect.left - margin.left;
                pinchRef.current = {
                  initialDist: Math.hypot(
                    t1.clientX - t0.clientX,
                    t1.clientY - t0.clientY,
                  ),
                  anchorX,
                  hasZoomed: false,
                };
              } else {
                const clientX = e.touches[0].clientX;
                setIsDragging(true);
                dragRef.current = { startX: clientX, moved: false };
                setActiveIndex(getNearestIdxFromClientX(clientX));
              }
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
              if (e.touches.length === 2 && pinchRef.current) {
                const t0 = e.touches[0],
                  t1 = e.touches[1];
                const dist = Math.hypot(
                  t1.clientX - t0.clientX,
                  t1.clientY - t0.clientY,
                );
                const ratio =
                  (dist - pinchRef.current.initialDist) /
                  pinchRef.current.initialDist;
                if (Math.abs(ratio) > 0.12 && !pinchRef.current.hasZoomed) {
                  const midClientX = (t0.clientX + t1.clientX) / 2;
                  const anchorIdx = getNearestIdxFromClientX(midClientX);
                  if (ratio > 0) onZoomIn?.(anchorIdx);
                  else onZoomOut?.(anchorIdx);
                  pinchRef.current = {
                    initialDist: dist,
                    anchorX: pinchRef.current.anchorX,
                    hasZoomed: true,
                  };
                }
              } else if (e.touches.length === 1) {
                if (dragRef.current) {
                  dragRef.current.moved = true;
                  // Do NOT call onPan here — panning shifts the xScale domain which
                  // makes getNearestIdxFromClientX return the same index as the
                  // viewport follows the finger, causing activeIndex to appear frozen.
                  // Period navigation is handled by onSwipeLeft/Right on touchEnd.
                }
                setActiveIndex(getNearestIdxFromClientX(e.touches[0].clientX));
              }
            }}
            onTouchEnd={(e) => {
              e.stopPropagation();
              if (dragRef.current && e.changedTouches.length === 1) {
                const { startX } = dragRef.current;
                const dx = e.changedTouches[0].clientX - startX;
                if (Math.abs(dx) > SWIPE_THRESHOLD) {
                  if (dx < 0) onSwipeLeft?.();
                  else onSwipeRight?.();
                }
              }
              dragRef.current = null;
              pinchRef.current = null;
              setIsDragging(false);
              onPanEnd?.();
            }}
          />

          {/* Axes */}
          <AxisBottom
            top={yMax}
            scale={xScale}
            numTicks={
              isYearView ? 12 : Math.max(2, Math.min(10, Math.floor(xMax / 30)))
            }
            tickFormat={(d) => {
              const date = d as Date;
              return isYearView
                ? MONTH_LABELS[date.getMonth()]
                : String(date.getDate());
            }}
            stroke="#9ca3af"
            tickStroke="#9ca3af"
            tickLength={0}
            tickLabelProps={() => ({
              fill: "#111",
              fontSize: 10,
              textAnchor: "middle",
              dy: 4,
            })}
          />
          {/* Context label — visible range, same style as TherapySetVsDelivered */}
          {contextLabel && (
            <text
              x={xMax / 2}
              y={yMax + 50}
              fontSize={13}
              fontWeight={600}
              fill="#374151"
              textAnchor="middle"
            >
              {contextLabel}
            </text>
          )}
          <AxisLeft
            scale={yDuration}
            tickValues={durationTicks}
            stroke="#9ca3af"
            tickStroke="#9ca3af"
            tickLength={0}
            tickLabelProps={() => ({
              fill: "#111",
              fontSize: 10,
              textAnchor: "end",
              dx: -8,
            })}
            label="min"
            labelProps={{
              fill: "#9ca3af",
              fontSize: 10,
              textAnchor: "middle",
              dx: 6,
              dy: -4,
            }}
          />
          <AxisRight
            left={xMax}
            scale={yPressure}
            tickValues={pressureTicks}
            tickFormat={(v) => String(Math.round(Number(v)))}
            stroke="#9ca3af"
            tickStroke="#9ca3af"
            tickLength={0}
            tickLabelProps={(v) => ({
              // Pressure negatives are physically meaningless → fade them.
              // SpO₂ / HR / AQI lower values are real numbers → keep full colour.
              fill: showPressures && Number(v) < yCenter ? "#d1d5db" : "#111",
              fontSize: 10,
              textAnchor: "start",
              dx: 8,
            })}
            label={showPressures ? "cmH₂O" : (metricUnit ?? "")}
            labelProps={{
              fill: "#9ca3af",
              fontSize: 10,
              textAnchor: "middle",
              dx: -6,
              dy: 4,
            }}
          />
        </Group>
      </svg>
    </div>
  );
};

export default ClearPressureFlowGraph;
